import {
  AdditiveBlending,
  BufferAttribute,
  BufferGeometry,
  Color,
  Group,
  Matrix4,
  Mesh,
  Object3D,
  ShaderMaterial,
  Vector3,
} from 'three'
import { BoxSizeEnum, BoxTypeEnum } from '../core/enums'
import { convertPosition } from '../core/unity'
import type { Bus } from '../game/bus'
import type { BusSplineFollower } from '../game/busSplineFollower'
import {
  BUS_LIGHT_RAY,
  BUS_SOURCE_HALF_WIDTH,
  DEFAULT_BUS_BODY_TUNING,
  type BusBodyTuning,
} from '../game/tuning'
import type { AssetLibrary, ManifestNode } from './assetLibrary'
import { busBodyMaterials } from './palette'
import { buildPrefab, findNode } from './prefabBuilder'
import { copyShadowCasting } from './shadowCasting'
import { ToonMaterial } from './toonMaterial'

/**
 * The bus, drawn from the shipped art.
 *
 * Two things the original does that a single rigid mesh would miss:
 *
 *  1. **The body is three pieces.** `Parts/Head`, `Parts/Middle` and
 *     `Parts/Tail` each hold their own mesh, and each is a *per-size variant*:
 *     a Medium is `Bus_Front_1` + `BusCenter_2` + `Bus_Back_1`, a Small the
 *     `_0` set. They tile exactly — for a Medium, [-5.15, -0.96], [-0.96, 1.04]
 *     and [1.04, 5.46] — so the join is invisible while the pieces can be bent
 *     independently. `Bus_N_single` is *not* this body: it appears once in the
 *     prefab, under `Ice/Fake`, with its renderer switched off — because it is
 *     the **particle shape** `ice_vfx` emits its shards from, not a drawing
 *     (IC-6). `DRAWN_SUBTREES` leaving `Ice` out is right; "a mesh nobody
 *     draws" was the wrong reason for it.
 *  2. **A parked bus and a bus on the road are not drawn the same way.** Once a
 *     bus is riding a spline, `InitCapBodies` turns the static renderers off
 *     (`SetStaticHeadRenderersEnabled(false)`) and hands the body to
 *     `BusBodyMesh` components that regenerate it **bent along the curve**.
 *
 * The bend here is done on the CPU over the whole assembly: every part's
 * geometry is baked into bus-local space once, and each frame the vertices are
 * mapped from local Z onto whichever curve the bus is driving. Only buses
 * actually on a curve pay for it.
 */

/** Prefab per bus size. */
const PREFAB: Readonly<Record<BoxSizeEnum, string>> = {
  [BoxSizeEnum.Small]: 'ConveyorBox (S)',
  [BoxSizeEnum.Medium]: 'ConveyorBox (M)',
  [BoxSizeEnum.Large]: 'ConveyorBox (L)',
}

/**
 * Subtrees worth drawing. Everything else under the prefab is effects,
 * raycast anchors and state markers — smoke, skid, wind lines, the ice VFX —
 * which have no geometry of their own or belong to features not ported yet.
 *
 * `Accommodations` are **not seats**, despite the name: each is a hollow hoop
 * of body skin (half-width 1.94, no geometry across the centreline) and there
 * are 6 / 10 / 14 of them by size, tiled end to end. Together they are the
 * ribbed middle of the bus — the concertina you see between the nose and the
 * tail — and they sit just inside the body's own half-width of 2.17.
 */
export const DRAWN_SUBTREES = ['Parts', 'Lids', 'Accommodations']

/**
 * Nodes to leave out of those subtrees.
 *
 * `CarLights` ships `active: false` but the game **does** switch it on:
 * `BoxToConveyorMovementStart` calls `SetActive(busLights, true)` as the bus
 * pulls out of its slot. An earlier reading here said nothing ever enabled it,
 * from a search for `get_BusLights` — which has no callers, because the field
 * is written directly at `Bus+0x110`.
 *
 * Off again for now all the same: the light-ray mesh renders wrong. Drop
 * `CarLights` from this set to bring it back — the timing and the `lightsOn`
 * flag are already in place.
 */
const SKIP = new Set(['WindlinesSpeedy', 'IceFloorTrail', 'SmokeWhite', 'SmokeWhiteTrail'])
/** Under `CarLights`; shown from the moment the bus starts driving. */
const LIGHTS = /^(CarLights|SM_LightRay_Round)/

/**
 * Materials the bus's colour replaces, and what to replace them with.
 *
 * `Bus.SwitchColor` swaps the material outright. The prefab ships whichever
 * colour it was authored with — the Medium says `Bus_Yellow`, the seats
 * `BusAcc_Red` — and any of the eleven variants can take its place. `Bus`,
 * `Bus_0` and `Bus_1` are older materials on a different shader with no palette
 * at all; they were what the port used to draw, which is why the body looked
 * flat and had to be tinted.
 */
const BODY_MATERIAL = /^Bus(_\d+|_[A-Z]\w+)?$/
const SEAT_MATERIAL = /^BusAcc_/

/**
 * Which panels `Bus.OpenCover` actually takes off.
 *
 * It is **two** of the four cover transforms, not all of them:
 *
 *     SetActive(false) on Bus+0x38  =  headCoverTransform    -> Lid_Front   (📐)
 *     SetActive(false) on Bus+0x40  =  middleCoverTransform  -> Lid_Center (🔍)
 *
 * 📐 **Only the second is read.** Ghidra dropped the arguments of the first call
 * (`OpenCover__0x20fb054.c`:27 is a bare `SetActive();`); the middle one keeps
 * its literal at :79/:84. `false` for the head lid is recovered by elimination:
 * the prefab ships `Lid_Front` active and `OpenCover` is its only writer, so off
 * is the only change available. Worth the distinction because this block used to
 * present both as transcripts.
 *
 * `tailCoverTransform` (0x48 -> `Lid_Back`) is never touched, so the rear of
 * the bus keeps its roof for the whole run. Hiding it too — which this used to
 * do, on the guess that the two objects held all three lids — opens a bay the
 * shipped game keeps closed.
 */
const REMOVED_BY_OPEN_COVER = /^Lid_(Front|Center)/

/**
 * What `EnterSplineRideMode` switches off — see `Part.hiddenInRideMode`.
 *
 * ❓ PLACEHOLDER, and it now matches **nothing**. The original hides
 * `renderersToHideInRideMode` (`Bus` 0xE8), a `[SerializeField] List<Renderer>`
 * whose contents AssetRipper stripped along with every other MonoBehaviour
 * field, so what is in it cannot be read. This port guessed the rings and the
 * rear lid; both turned out to be driven by something else — the rings by their
 * own per-tween callbacks, the rear lid by the sequence's `OnComplete` (see
 * `TAIL_LID`). Left in place as a named hook rather than deleted, so the day
 * the list becomes readable there is somewhere to put it.
 */
const HIDDEN_IN_RIDE_MODE = /^$/

/**
 * 🔍 The rear lid drops when the **ring wave finishes**, not when the bus
 * reaches the road.
 *
 * `EnterSplineRideMode__0x20fc5ac.c` touches exactly three things — `arrow`
 * (0x28), `renderersToHideInRideMode` (0xE8) and `skids` (0x118). It never
 * reads 0x48. What does is `<PlayAccommodationAnimation>b__104_0__0x20fe440.c`,
 * whose whole body is
 *
 *     if (tailCoverTransform != null) tailCoverTransform.gameObject.SetActive(false);
 *
 * and which `PlayAccommodationAnimation` hangs on `_accommodationSequence`
 * (0x170) as the sequence's own `OnComplete`. `dump.cs:709406` names 0x48
 * `tailCoverTransform`.
 *
 * So the bay at the back stays shut for the whole unfolding wave and opens on
 * the last frame of it. This port dropped it the instant the bus took the
 * spline, which is `accommodationAnimationSeconds` too early.
 */
const TAIL_LID = /^Lid_Back/

/**
 * Is `Lid_Back` still on screen?
 *
 * Pure, so a headless test can hold the rule still — the last three items all
 * shipped a view-only mistake that nothing could reach.
 *
 * Three cases, all read off `PlayAccommodationAnimation__0x20fc0dc.c`:
 *
 *  - `rideTime < 0` — the wave has not started (the bus is still in its slot,
 *    `Initialize` has not run), so the lid is on. `PlayAccommodationAnimation`
 *    is called from `BusSplineFollower.Initialize`, nowhere earlier.
 *  - `ringCount === 0` — the lid is already gone, but **not** by the route this
 *    used to cite. `PlayAccommodationAnimation`:37 gates on null-or-`Count == 0`
 *    and takes an **early exit** that does `tailCoverTransform.SetActive(false)`
 *    inline (:51-85) and returns, *before* `_accommodationSequence` is killed or
 *    built (:91-99). The `if (count < 2)` at :103-105 that was quoted here is
 *    only reachable with `Count >= 1`, so it never sees zero at all.
 *  - otherwise the sequence runs exactly `total`: the last ring starts at
 *    `(count - 1) * stagger` and lasts `morph`, and
 *    `(count - 1) · (total - morph)/(count - 1) + morph` is `total` by
 *    construction, whatever `count` and `RING_MORPH_FRACTION` are.
 */
export function tailLidVisible(rideTime: number, total: number, ringCount: number): boolean {
  if (rideTime < 0) return true
  if (ringCount === 0) return false
  return rideTime < total
}

/**
 * Where a ring's local Z lands on the curve — measured back from the **drawn
 * tail**, so the concertina stays put while the nose pulls away.
 *
 * Pure and exported so a test can hold the anchoring still without building a
 * bus; see the long note at its call site for why it is the tail and not the
 * nose. When the bus is not stretched (`roadMiddle + capBackRoom == span -
 * capFront`) this equals the old nose-relative `z - minZ - capFront`.
 */
export function ringAlong(
  z: number,
  minZ: number,
  span: number,
  roadMiddle: number,
  capBackRoom: number,
): number {
  return z - minZ - span + roadMiddle + capBackRoom
}

/**
 * Where frame `f` of the body table sits **along the curve**.
 *
 * Even spacing in `along`, so every frame covers the same arc — see the block
 * in `poseBent` for why that is the original's resolution and not this port's
 * old one. Pure, and exported with its inverse so a test can hold the pair
 * still without an asset library.
 */
export function frameAlong(f: number, alongMin: number, alongSpan: number): number {
  return alongMin + (alongSpan * f) / FRAMES
}

/** The inverse: which frame a point at `along` falls between. Not clamped. */
export function frameIndex(along: number, alongMin: number, alongSpan: number): number {
  return ((along - alongMin) / alongSpan) * FRAMES
}

/**
 * 🔍 `_spineCurvatures[i]` — how hard the curve turns at frame `i`, signed.
 *
 * `ApplyDeformedSegments__0x2101184.c`:556-655, per row:
 *
 *     step = |pos[i] - pos[i-1]|;
 *     if (step < 0.0001f) { k = 0; }
 *     else {
 *         A = _spineForwards[i-1];  B = _spineForwards[i];
 *         cos = Mathf.Clamp(Dot(A, B) / sqrt(A.sqrMagnitude * B.sqrMagnitude), -1, 1);
 *         deg = Mathf.Acos(cos) * 57.29578f;
 *         if (Dot(Cross(A, B), N) < 0) deg = -deg;
 *         k = deg * 0.017453292f / step;
 *     }
 *
 * Radians of turn per unit of arc, i.e. 1/R on a circle of radius R — and
 * **signed**, which is the whole point: `_spinePosK` and `_spineNegK` are built
 * from it, and `b2.2` narrows the outside of a bend differently from the
 * inside.
 *
 * Two details worth keeping verbatim. The original converts to degrees and
 * straight back (`57.29578 * 0.017453292` = 0.99999996), so the pair is copied
 * rather than cancelled — a reader comparing the two texts should not have to
 * wonder. And `N` is the row normal read out of a static `Vector3`; the port's
 * frames are flattened to the ground plane (`_forward.y = 0` in `poseBent`), so
 * `N` is `up` and the sign is just which way the curve bends in plan view.
 */
export function frameCurvature(
  ax: number,
  az: number,
  bx: number,
  bz: number,
  step: number,
): number {
  if (step < 1e-4) return 0
  const scale = Math.sqrt((ax * ax + az * az) * (bx * bx + bz * bz))
  if (scale < 1e-15) return 0
  const cos = Math.min(1, Math.max(-1, (ax * bx + az * bz) / scale))
  // `Cross(A, B) . up` with both vectors in the ground plane is the y term
  // alone, and in three's left-handed-Z view of Unity space that is `az*bx -
  // ax*bz`. Only its sign is used.
  const deg = Math.acos(cos) * 57.29578 * (az * bx - ax * bz < 0 ? -1 : 1)
  return (deg * 0.017453292) / step
}

/**
 * 🔍 `_spinePosK` / `_spineNegK` — the running max and min of `k` over each
 * frame and the one before it.
 *
 * `ApplyDeformedSegments`:668-710, with `SPINE_RES = 32` so `i` runs 0..32:
 *
 *     a = k[i - (i != 0 ? 1 : 0)];
 *     b = k[i == 32 ? 31 : i];
 *     posK[i] = Mathf.Max(a, b);
 *     negK[i] = Mathf.Min(a, b);
 *
 * A two-tap dilation and erosion, with both ends clamped so the first and last
 * entries repeat their neighbour rather than reading off the array. Writes into
 * `posK` and `negK`, which must be at least `count` long.
 */
export function curvatureEnvelope(
  k: Float32Array,
  posK: Float32Array,
  negK: Float32Array,
  count: number,
): void {
  const last = count - 1
  for (let i = 0; i < count; i++) {
    const a = k[i === 0 ? 0 : i - 1]
    const b = k[i === last ? last - 1 : i]
    posK[i] = Math.max(a, b)
    negK[i] = Math.min(a, b)
  }
}

/**
 * 🔍 `BusBodyMesh.SoftLimit(v, lim)` — `SoftLimit__0x2103814.c`, whole:
 *
 *     float lo = lim * 0.85f;
 *     if (lo < v) {
 *         float r = (v - lo) / (lim - lo);
 *         v = lo + (lim - lo) * (r / (r + 1f));
 *     }
 *     return v;
 *
 * Below 85 % of the limit nothing happens at all; above it the remaining 15 %
 * is compressed by `r/(r+1)`, which approaches 1 but never reaches it. So the
 * body never quite touches its own limit and never creases — a hard clamp
 * would flatten every vertex past the line onto it.
 */
export function softLimit(v: number, lim: number): number {
  const lo = lim * 0.85
  if (lo < v) {
    const r = (v - lo) / (lim - lo)
    return lo + (lim - lo) * (r / (r + 1))
  }
  return v
}

/**
 * 🔍 `BusSplineFollower.UpdateClearanceProbes` — `__0x210e800.c`, whole.
 *
 * A 49-sample picture of the curve the bus is actually driving, taken over the
 * bus's own extent widened by both caps. `b2.4`'s pinch pass measures the 33
 * spine rows against this set: where the curve doubles back close to itself,
 * the body has to be squeezed so the two passes do not intersect.
 *
 * The original stores it three times over — `SetClearanceProbes` is called on
 * `middleBody` (0xA8) and `headBody` (0xB0), and **inlined** for `tailBody`
 * (0xB8), which writes 0x108..0x138 in place instead of calling. All three
 * body meshes get the same probe set; the third one is not missing, it is the
 * compiler. (CLAUDE.md §8: no call site is not the same as dead code.)
 *
 * The order of the guards, verbatim:
 *
 *     if (_currentSpline == null) return;
 *     if (_bus == null) return;
 *     if (_cachedCurrentSplineLength < 1f) return;          // :0x94..0xd8
 *
 *     double back = _tailCapWorldLength > 0.01f ? _tailCapWorldLength / len : 0;
 *     double fore = _headCapWorldLength > 0.01f ? _headCapWorldLength / len : 0;
 *     double start = _tailPercent - back;
 *     double end   = _headPercent + fore;
 *     if (!_currentSpline.isClosed) {
 *         start = start < 0 ? 0 : start;
 *         end   = end > 1 ? 1 : end;
 *         span  = end - start;
 *     } else { ...wrap start into [0,1), span = end - start, span += span < 0 ? 1 : 0... }
 *
 *     if (span <= 0.0005) { middleBody.ClearClearanceProbes();
 *                           headBody.ClearClearanceProbes();
 *                           tailBody._probesValid = false; return; }
 *
 * ⚠️ The short-spline guard **returns without clearing**. A bus whose curve is
 * under a unit long keeps whatever probe set it last had, stale positions and
 * all, and `_probesValid` stays true. That is the original's behaviour, not an
 * oversight of the port — the two early-outs above it do the same.
 *
 * ⚠️ `_probesValid` is written twice with two different thresholds and only one
 * of them is observable. `SetClearanceProbes` ends with
 *
 *     valid = world != null && forwards != null && arcs != null
 *             && span > 0.0001 && count > 1;
 *
 * but the `span <= 0.0005` gate above has already returned for everything at or
 * below 0.0005, so by the time `0.0001` is tested it cannot be false. **0.0005
 * is the gate that exists**; 0.0001 is unreachable-as-false and is kept here
 * only so the text matches. `count > 1` is likewise always true at 49. The one
 * test that pins the boundary therefore pins 0.0005.
 *
 * The port's splines are all open — `SplineIsClosed` is false on all forty
 * levels, see `bodyPose` — so the wrapping branch is unreachable here and is
 * left out rather than written and never run.
 */
export const PROBE_COUNT = 49

/** `_probeWorld` / `_probeForwards` / `_probeArcs` and the three scalars beside them. */
export interface ClearanceProbes {
  /** `_probeWorld` (0x108) — `PROBE_COUNT` positions, three floats each. */
  world: Float32Array
  /** `_probeForwards` (0x110) — flat unit directions, three floats each. */
  forwards: Float32Array
  /** `_probeArcs` (0x118) — arc length from probe 0, so `arcs[0] === 0`. */
  arcs: Float32Array
  /** `_probeStartPercent` (0x128). */
  startPercent: number
  /** `_probeSpanPercent` (0x130). */
  spanPercent: number
  /** `_probesValid` (0x138). */
  valid: boolean
}

export function createClearanceProbes(): ClearanceProbes {
  return {
    world: new Float32Array(PROBE_COUNT * 3),
    forwards: new Float32Array(PROBE_COUNT * 3),
    arcs: new Float32Array(PROBE_COUNT),
    startPercent: 0,
    spanPercent: 0,
    valid: false,
  }
}

const _probePos = new Vector3()

/**
 * Fill `out` from the curve. `sample` is the spline read, in **three** space —
 * the caller has already been through `convertPosition`, as `fill` has.
 *
 * `splineLength`, `headPercent` and `tailPercent` are the follower's
 * `_cachedCurrentSplineLength` (0xA4), `_headPercent` (0x88) and `_tailPercent`
 * (0x90); head is the nose and tail is the back, in the original and here
 * (CLAUDE.md §8).
 */
export function updateClearanceProbes(
  out: ClearanceProbes,
  splineLength: number,
  headPercent: number,
  tailPercent: number,
  headCapWorldLength: number,
  tailCapWorldLength: number,
  sample: (percent: number, into: Vector3) => void,
): void {
  // Leaves the previous set in place, `valid` included — see the note above.
  if (!(splineLength >= 1)) return

  const back = tailCapWorldLength > 0.01 ? tailCapWorldLength / splineLength : 0
  const fore = headCapWorldLength > 0.01 ? headCapWorldLength / splineLength : 0
  const start = Math.max(0, tailPercent - back)
  const end = Math.min(1, headPercent + fore)
  const span = end - start

  out.startPercent = start
  out.spanPercent = span
  if (span <= 0.0005) {
    out.valid = false
    return
  }

  const { world, forwards, arcs } = out
  const last = PROBE_COUNT - 1
  let arc = 0
  arcs[0] = 0
  for (let i = 0; i < PROBE_COUNT; i++) {
    // `start + span * i / 48`, then the same `t > 1 ? ... : t` the original
    // applies to every sample. `end` is already clamped to 1, so the ceiling
    // only ever catches float drift on the last probe.
    const t = Math.min(1, start + (span * i) / last)
    sample(t, _probePos)
    const b = i * 3
    world[b] = _probePos.x
    world[b + 1] = _probePos.y
    world[b + 2] = _probePos.z
    if (i > 0) {
      const a = b - 3
      const dx = world[b] - world[a]
      const dy = world[b + 1] - world[a + 1]
      const dz = world[b + 2] - world[a + 2]
      // Full 3D distance, not the flattened one the forwards use.
      arc += Math.sqrt(dx * dx + dy * dy + dz * dz)
      arcs[i] = arc
    }
  }

  // `forwards[i]` looks one probe ahead, and the last one looks at itself —
  // `lVar3 = lVar8 != 0x234 ? uVar7 + 1 : 0x30` — so it is always degenerate
  // and always takes the fallback. Both fallbacks in the decompile are the
  // inlined body of `Vector3.Normalize`: `Vector3.forward` when the flattened
  // difference is under 0.0001 squared, `Vector3.zero` when the magnitude is
  // under 1e-05, which the first test has already excluded. Unity's forward is
  // +Z, so in three it is -Z (`convertPosition` negates z).
  for (let i = 0; i < PROBE_COUNT; i++) {
    const a = i * 3
    const b = (i === last ? last : i + 1) * 3
    // y is dropped, not projected: the original writes `0f / mag` into it.
    const dx = world[b] - world[a]
    const dz = world[b + 2] - world[a + 2]
    const sq = dx * dx + dz * dz
    if (sq <= 0.0001) {
      forwards[a] = 0
      forwards[a + 1] = 0
      forwards[a + 2] = -1
    } else {
      const inv = 1 / Math.sqrt(sq)
      forwards[a] = dx * inv
      forwards[a + 1] = 0
      forwards[a + 2] = dz * inv
    }
  }

  // `span > 0.0001 && count > 1`, both already true past the 0.0005 gate.
  out.valid = true
}

/**
 * 🔍 Phase 1 of the three, the **self-pinch** — `ApplyDeformedSegments`:768-957,
 * the `if (!_probesValid)` half of the pinch.
 *
 * Where the curve doubles back so that two rows of the same body end up beside
 * each other, each of the two has to give way or the mesh drives through
 * itself. Every ordered pair of rows is tested; three gates have to agree
 * before anything is narrowed, and all three come from `dump.cs`'s own named
 * constants (`BusBodyMesh`, the const block under the field list):
 *
 *     for (i = 0; i < SPINE_RES; i++)
 *       for (j = i + 1; j <= SPINE_RES; j++) {
 *         float d = Vector3.Distance(_spinePositions[i], _spinePositions[j]);
 *         if (d >= _sourceHalfWidth * 2.1f) continue;                    // close
 *         if (d * FoldBackArcRatio > _spineArc[j] - _spineArc[i]) continue; // detour
 *         if (Vector3.Dot(_spineForwards[i], _spineForwards[j]) > OpposedHeadingDot) continue;
 *         ...narrow row i in i's frame, then row j in j's frame...
 *       }
 *
 * The three read as one sentence: **near in space, far along the body, and
 * pointing against each other**. `FoldBackArcRatio` is what keeps neighbouring
 * rows out of it without a special case — for rows a step apart the arc between
 * them *is* the distance, and `1.3 × d <= d` is false. The same triple is what
 * `npm run check:bodyfold` measures, and `_sourceHalfWidth * 2.1` is the radius
 * `b2.0` pinned (2.1697 × 2.1 = 4.556).
 *
 * Then, for each endpoint of a surviving pair, measured in **that** row's own
 * frame (`:846-882` for `i`, `:900-942` for `j`, the second with the sign
 * flipped because it looks back the other way):
 *
 *     float lateral = Vector3.Dot(other - self, rot[self] * Vector3.right);
 *     if (lateral > d * 0.6f) {
 *         lim = lateral * 0.5f - ClearChannel;
 *         if (MinPinchWall <= lim && lim < clearPos[self]) clearPos[self] = lim;
 *     } else if (lateral < d * -0.6f) {
 *         lim = lateral * -0.5f - ClearChannel;
 *         if (MinPinchWall <= lim && lim < clearNeg[self]) clearNeg[self] = lim;
 *     }
 *
 * ⚠️ **Three branches, and the middle one writes nothing.** A pair that is
 * near, opposed and long-way-round but sits within `±0.6 d` of dead ahead —
 * one row directly in front of the other rather than beside it — is left
 * alone. Ghidra spells this as a pair of booleans (`bVar6 == bVar8`) and it is
 * easy to read as a two-way split; it is not.
 *
 * ⚠️ **`MinPinchWall` refuses the write, it does not floor it.** `if (0.6 <=
 * lim)` — a pinch that would take the wall under 0.6 is **discarded**, so the
 * body keeps its full width there and simply intersects. Clamping to 0.6
 * instead would narrow rows the original never touches.
 *
 * ⚠️ Which named 0.6 the `d * 0.6f` in the branch test came from is not
 * recoverable — `MinPinchWall` and `ClearSlope` are both 0.6 and IL2CPP has
 * erased the difference. It does not matter for behaviour, only for the name.
 *
 * `lateral` is a full 3D dot in the original; the port's row axes are flattened
 * to the ground plane (`_forward.y = 0` in `fill`), so the `y` term drops out
 * on its own rather than being dropped by hand.
 *
 * The port runs `count = FRAMES + 1 = 49` rows against the original's 33. As
 * with the curvature cap, every quantity here is pairwise geometry — distance,
 * arc, dot, lateral offset — so the rule carries; only the literal loop bounds
 * `0..32` do not, and they are row counts.
 */
export function selfPinch(
  frames: Float32Array,
  framesFz: Float32Array,
  stepLength: Float32Array,
  clearPos: Float32Array,
  clearNeg: Float32Array,
  count: number,
  halfWidth: number,
): void {
  if (count < 2) return
  const foldRadius = halfWidth * FOLD_RADIUS_RATIO

  // `_spineArc` (0x1E8), built at `:719-753` as the running sum of the gaps
  // between consecutive rows — which is exactly what `stepLength` holds.
  if (arcScratch.length < count) arcScratch = new Float64Array(count)
  const arc = arcScratch
  arc[0] = 0
  for (let i = 1; i < count; i++) arc[i] = arc[i - 1] + stepLength[i - 1]

  for (let i = 0; i + 1 < count; i++) {
    const a = i * 6
    for (let j = i + 1; j < count; j++) {
      const b = j * 6
      const dx = frames[b] - frames[a]
      const dy = frames[b + 1] - frames[a + 1]
      const dz = frames[b + 2] - frames[a + 2]
      const d = Math.sqrt(dx * dx + dy * dy + dz * dz)
      if (!(d < foldRadius)) continue
      if (d * FOLD_BACK_ARC_RATIO > arc[j] - arc[i]) continue
      const dot = frames[a + 5] * frames[b + 5] + framesFz[i] * framesFz[j]
      if (dot > OPPOSED_HEADING_DOT) continue

      // Row `i` looks toward `j`; row `j` looks back, hence the negated delta.
      pinchRow(dx, dz, d, i, frames, clearPos, clearNeg)
      pinchRow(-dx, -dz, d, j, frames, clearPos, clearNeg)
    }
  }
}

/**
 * 🔍 The other half of phase 1 — the **probe pinch**, `ApplyDeformedSegments`
 * :958-1211, the `_probesValid` branch. Mutually exclusive with `selfPinch`:
 * the original runs this one whenever the probe set is good and falls back to
 * the self-pinch when it is not.
 *
 * The self-pinch asks whether the body doubles back on **itself**. This one
 * asks whether it doubles back on the **road it is driving**, which is a wider
 * question: the probe window reaches past both ends of the body by a cap
 * length, so a nose about to swing into a bend the tail is still in gets caught
 * before any part of the body has reached it.
 *
 * Same three gates and the same three-branch lateral law as the self-pinch, and
 * three differences that matter:
 *
 *  1. **The arc gate takes an absolute value** (`:1136-1140`). A probe can lie
 *     *behind* the row as easily as ahead of it, where the self-pinch only ever
 *     looked forward (`j > i`, so `arc[j] - arc[i]` was already positive).
 *  2. **Only the row is narrowed.** A probe is a point on the curve, not a row
 *     of the body; there is nothing on its side to give way.
 *  3. **The row has to be located on the probe polyline first**, and the
 *     original does it through the arc table rather than by measuring
 *     (`:1011-1098`):
 *
 *         double off = _spinePercents[i] - _probeStartPercent;
 *         if (off < -0.5) off += 1.0;                  // the seam
 *         off = off < 0 ? 0 : off;
 *         if (!(off < span)) off = span;
 *         float f = (count - 1) * (float)(off / span);
 *         int idx = (int)f;
 *         if (idx >= count - 1) idx = count - 2;
 *         rowArc = _probeArcs[idx] + (f - idx) * (_probeArcs[idx + 1] - _probeArcs[idx]);
 *
 *     ⚠️ The high clamp is written `off < span ? off : span`, so an `off`
 *     **equal** to the span keeps its value rather than being replaced — the
 *     same number either way, but the NaN handling differs and the decompile
 *     spends four booleans on it.
 *
 *     ⚠️ `off += 1.0` only when `off < -0.5`, not whenever it is negative. A row
 *     a little behind the window start stays negative and is clamped to 0; only
 *     one that has wrapped the whole way round the spline is brought forward.
 *     The port's splines are open so the wrap never fires, but the low clamp
 *     does, and dropping it would index off the front of the arc table.
 *
 * The original works in the mesh's local space — `_probeLocal` (0x140) is
 * `_probeWorld` put through `Matrix4x4.MultiplyPoint3x4` — because
 * `_spinePositions` are local. Distance, dot and the lateral projection are all
 * unchanged by a rigid transform, and both sides of the comparison are already
 * in the same space in the port, so no transform is needed here. That holds
 * only while the bus root is unscaled; it is, and `busGeometry.test.ts` is
 * where that would break first.
 */
export function probePinch(
  frames: Float32Array,
  framesFz: Float32Array,
  rowPercent: Float64Array,
  probes: ClearanceProbes,
  clearPos: Float32Array,
  clearNeg: Float32Array,
  count: number,
  halfWidth: number,
): void {
  if (!probes.valid) return
  const span = probes.spanPercent
  if (!(span > 0)) return
  const foldRadius = halfWidth * FOLD_RADIUS_RATIO
  const { world, forwards, arcs } = probes
  const last = PROBE_COUNT - 1

  for (let i = 0; i < count; i++) {
    let off = rowPercent[i] - probes.startPercent
    if (off < -0.5) off += 1
    if (off < 0) off = 0
    if (!(off < span)) off = span
    const f = Math.fround(last * (off / span))
    let idx = f | 0
    if (idx >= last) idx = last - 1
    const rowArc = arcs[idx] + (f - idx) * (arcs[idx + 1] - arcs[idx])

    const a = i * 6
    for (let j = 0; j < PROBE_COUNT; j++) {
      const b = j * 3
      const dx = world[b] - frames[a]
      const dy = world[b + 1] - frames[a + 1]
      const dz = world[b + 2] - frames[a + 2]
      const d = Math.sqrt(dx * dx + dy * dy + dz * dz)
      if (!(d < foldRadius)) continue
      if (d * FOLD_BACK_ARC_RATIO > Math.abs(rowArc - arcs[j])) continue
      const dot = frames[a + 5] * forwards[b] + framesFz[i] * forwards[b + 2]
      if (dot > OPPOSED_HEADING_DOT) continue

      // The delta runs probe-minus-row here, where the self-pinch ran
      // other-row-minus-row. Same direction: toward the thing being avoided.
      pinchRow(dx, dz, d, i, frames, clearPos, clearNeg)
    }
  }
}

/** One endpoint of a surviving pair, narrowed in its own row's frame. */
function pinchRow(
  dx: number,
  dz: number,
  d: number,
  row: number,
  frames: Float32Array,
  clearPos: Float32Array,
  clearNeg: Float32Array,
): void {
  const r = row * 6
  // `rot[row] * Vector3.right`, which `fill` has already stored as the row's
  // right axis in `frames[+3]` (x) and `frames[+4]` (z).
  const lateral = dx * frames[r + 3] + dz * frames[r + 4]
  if (lateral > d * 0.6) {
    const lim = lateral * 0.5 - CLEAR_CHANNEL
    if (MIN_PINCH_WALL <= lim && lim < clearPos[row]) clearPos[row] = lim
  } else if (lateral < d * -0.6) {
    const lim = lateral * -0.5 - CLEAR_CHANNEL
    if (MIN_PINCH_WALL <= lim && lim < clearNeg[row]) clearNeg[row] = lim
  }
  // The band between is the third branch, and it writes nothing.
}

let arcScratch = new Float64Array(64)

/** 🔍 `BusBodyMesh.ClearChannel` — `private const float ClearChannel = 0.35;`. */
const CLEAR_CHANNEL = 0.35
/** 🔍 `BusBodyMesh.MinPinchWall` — `private const float MinPinchWall = 0.6;`. */
const MIN_PINCH_WALL = 0.6
/** 🔍 `BusBodyMesh.OpposedHeadingDot` — `private const float OpposedHeadingDot = -0.25;`. */
const OPPOSED_HEADING_DOT = -0.25
/** 🔍 `BusBodyMesh.FoldBackArcRatio` — `private const float FoldBackArcRatio = 1.3;`. */
const FOLD_BACK_ARC_RATIO = 1.3
/**
 * 📐 The fold radius, `_sourceHalfWidth * 2.1f` (`ApplyDeformedSegments`:718).
 * A bare literal beside the named constants above — it has no name in
 * `dump.cs`. `b2.0` measured what it comes to: 2.1697 × 2.1 = 4.556.
 */
const FOLD_RADIUS_RATIO = 2.1

/**
 * 🔍 How wide the body may be at each frame, left and right — `_spineClearPos`
 * (0x1F0) and `_spineClearNeg` (0x1F8).
 *
 * `ApplyDeformedSegments` builds them in three phases, and the order matters
 * because each one only ever **narrows** what the previous left:
 *
 *  1. `:756-1214` the pinch, against the clearance probes and against the body
 *     itself. **Not here** — that is `b2.4`, and it needs the probe set.
 *  2. `:1216-1245` the curvature cap:
 *
 *         if (posK[i] > 0.0001f) {
 *             lim = 0.9f / posK[i];
 *             if (lim < _sourceHalfWidth && lim < clearPos[i]) clearPos[i] = lim;
 *         }
 *         if (negK[i] < -0.0001f) {
 *             lim = -0.9f / negK[i];
 *             if (lim < _sourceHalfWidth && lim < clearNeg[i]) clearNeg[i] = lim;
 *         }
 *
 *     `0.9 / k` is 90 % of the turn radius. Past that the inside of the bend
 *     folds through itself, so this is the width a bend of that radius can
 *     physically carry.
 *  3. `:1247-1346` two slope-limiting passes, forward then backward, with
 *     `ClearSlope = 0.6` (`dump.cs`, `BusBodyMesh` const):
 *
 *         for (i = 0; i < 32; i++) {
 *             d = |pos[i] - pos[i+1]| * 0.6f;
 *             if (clearPos[i] + d < clearPos[i+1]) clearPos[i+1] = clearPos[i] + d;
 *             ...same for clearNeg...
 *         }
 *         for (j = 32; j >= 1; j--) {
 *             d = |pos[j-1] - pos[j]| * 0.6f;
 *             if (clearPos[j] + d < clearPos[j-1]) clearPos[j-1] = clearPos[j] + d;
 *             ...
 *         }
 *
 *     A narrow frame drags its neighbours in, at most 0.6 units of width per
 *     unit of length, so the taper is a ramp rather than a step. Both
 *     directions, because a pinch has to be approached from either side.
 *
 * The channels start at `halfWidth * 4` (`:716`), which is not a width so much
 * as "no limit": every source vertex is inside `halfWidth`, and
 * `softLimit(x, 4 * halfWidth)` leaves anything below `3.4 * halfWidth` alone.
 *
 * ⚠️ The port runs `FRAMES + 1 = 49` rows where the original runs `SPINE_RES +
 * 1 = 33`. The slope limit is per **unit of length**, not per row, so the two
 * converge; the curvature cap is per row and is likewise a pointwise function
 * of `posK`/`negK`. What does not carry over is the literal `32` in the
 * decompiled loop bounds — those are row counts, and using them here would
 * leave the last sixteen frames untouched.
 */
export function clearanceChannels(
  posK: Float32Array,
  negK: Float32Array,
  stepLength: Float32Array,
  clearPos: Float32Array,
  clearNeg: Float32Array,
  count: number,
  halfWidth: number,
  /**
   * Row geometry for phase 1. Omitted, the pinch is skipped — which is what a
   * caller that only wants the pointwise phases (a test, a diagnostic) gets.
   */
  frames?: Float32Array,
  framesFz?: Float32Array,
  /**
   * The probe set and each row's percent on the same spline. Present and valid,
   * phase 1 is the probe pinch; absent or invalid, it is the self-pinch.
   */
  probes?: ClearanceProbes,
  rowPercent?: Float64Array,
): void {
  const open = halfWidth * 4
  for (let i = 0; i < count; i++) {
    clearPos[i] = open
    clearNeg[i] = open
  }

  // Phase 1 — the pinch. Runs first because each phase only ever narrows what
  // the last one left. The two halves are mutually exclusive in the original
  // and are here too: a good probe set measures the body against the curve it
  // is driving, and only when there is none does it fall back to measuring the
  // body against itself.
  if (frames && framesFz) {
    if (probes?.valid && rowPercent) {
      probePinch(frames, framesFz, rowPercent, probes, clearPos, clearNeg, count, halfWidth)
    } else {
      selfPinch(frames, framesFz, stepLength, clearPos, clearNeg, count, halfWidth)
    }
  }

  // Phase 2 — the curvature cap.
  for (let i = 0; i < count; i++) {
    if (posK[i] > 0.0001) {
      const lim = 0.9 / posK[i]
      if (lim < halfWidth && lim < clearPos[i]) clearPos[i] = lim
    }
    if (negK[i] < -0.0001) {
      const lim = -0.9 / negK[i]
      if (lim < halfWidth && lim < clearNeg[i]) clearNeg[i] = lim
    }
  }

  // Phase 3 — slope limit, forward then backward. `stepLength[i]` is the gap
  // between rows `i` and `i + 1`.
  for (let i = 0; i + 1 < count; i++) {
    const d = stepLength[i] * CLEAR_SLOPE
    if (clearPos[i] + d < clearPos[i + 1]) clearPos[i + 1] = clearPos[i] + d
    if (clearNeg[i] + d < clearNeg[i + 1]) clearNeg[i + 1] = clearNeg[i] + d
  }
  for (let j = count - 1; j >= 1; j--) {
    const d = stepLength[j - 1] * CLEAR_SLOPE
    if (clearPos[j] + d < clearPos[j - 1]) clearPos[j - 1] = clearPos[j] + d
    if (clearNeg[j] + d < clearNeg[j - 1]) clearNeg[j - 1] = clearNeg[j] + d
  }
}

/** 🔍 `BusBodyMesh.ClearSlope` — `private const float ClearSlope = 0.6;`. */
const CLEAR_SLOPE = 0.6

/**
 * `Bus.PlayAccommodationAnimation(totalDuration)`, called from
 * `BusSplineFollower.Initialize` — the only animation the original plays over
 * the parked-to-road handover.
 *
 *     stagger = (total - total * 0.4) / (count - 1)
 *     for each accommodation, at t = i * stagger:
 *         DOVirtual.Float(0, 200, total * 0.4, w => SetBlendShapeWeight(idx, w))
 *             .OnComplete(() => SetActive(false))
 *
 * So each ring plays its `Anim` blend shape and then switches itself off, one
 * after another down the bus — a wave, not a single cut. The lids are *not* part
 * of it: `OpenCover` is a bare `SetActive(false)` with no tween, so those do go
 * in one frame, in the shipped game as here.
 *
 * `totalDuration` is a `[SerializeField]` and did not survive, so it is a HUD
 * slider like the rest. `RING_MORPH_FRACTION` is the literal 0.4 above.
 */
const RING_MORPH_FRACTION = 0.4

/**
 * A mesh's blend shape deltas, moved into bus-local space.
 *
 * Deltas are directions, so only the linear part of the bake matrix applies —
 * running them through the translation as well would drag every vertex to the
 * part's own offset the moment the weight left zero.
 */
function bakeMorph(
  library: AssetLibrary,
  meshName: string | null,
  matrix: Matrix4,
): Float32Array | null {
  if (!meshName) return null
  const raw = library.blendShape(meshName)
  if (!raw) return null
  const e = matrix.elements
  const out = new Float32Array(raw.length)
  for (let i = 0; i < raw.length; i += 3) {
    const x = raw[i]
    const y = raw[i + 1]
    const z = raw[i + 2]
    out[i] = e[0] * x + e[4] * y + e[8] * z
    out[i + 1] = e[1] * x + e[5] * y + e[9] * z
    out[i + 2] = e[2] * x + e[6] * y + e[10] * z
  }
  return out
}

/**
 * The part's rest pose with its blend shape applied at the current weight.
 *
 * Unity weights run 0–100 for the full shape, and the tween drives 0–200, so
 * the shape overshoots to twice its authored offset — which is what the
 * original does, `SetBlendShapeWeight` extrapolating past `fullWeights`.
 */
/**
 * The per-vertex road distance buffer, made on first use.
 *
 * Lazily, like the seam twin and for the same reason: a bus that never rides
 * the spline — every parked one — never comes through `poseBranch` and has no
 * business carrying the attribute. A geometry without it draws with the plain
 * material, which does not declare `USE_ROAD_CUT` and never reads it.
 */
function roadDistanceAttribute(geometry: BufferGeometry, count: number): Float32Array {
  const existing = geometry.getAttribute('aRoadDistance') as BufferAttribute | undefined
  if (existing && existing.count === count) return existing.array as Float32Array
  const array = new Float32Array(count)
  geometry.setAttribute('aRoadDistance', new BufferAttribute(array, 1))
  return array
}

/**
 * Is this mesh under one of the three transforms `ExitHiddenState` scales —
 * `Parts/Head` (`headPart`), `Parts/Middle` (`middleBody`), `Parts/Tail`
 * (`tailPart`)? Asked of the prefab tree as built, before the pieces are
 * flattened onto `root`. `CarLights` hangs under `Head` in the prefab and so
 * bounces with it, as it does in the original.
 */
function underBouncePart(mesh: Mesh, root: Object3D): boolean {
  for (let p = mesh.parent; p && p !== root; p = p.parent) {
    if (BOUNCE_PARTS.has(p.name) && p.parent?.name === 'Parts') return true
  }
  return false
}

/** The node names under `Parts` that `ExitHiddenState` scales. */
const BOUNCE_PARTS: ReadonlySet<string> = new Set(['Head', 'Middle', 'Tail'])

/**
 * Normals under a `(1, sy, 1)` scale: the inverse transpose divides Y by `sy`;
 * renormalise after. In place.
 */
function scaleNormalsY(normals: Float32Array, sy: number): void {
  for (let i = 0; i < normals.length; i += 3) {
    const ny = normals[i + 1] / sy
    const len = Math.hypot(normals[i], ny, normals[i + 2]) || 1
    normals[i] /= len
    normals[i + 1] = ny / len
    normals[i + 2] /= len
  }
}

function restOf(part: Part): Float32Array {
  const delta = part.morphDelta
  // Length mismatch means the deltas were built against a different expansion
  // of the mesh; adding them would write NaN into every position from there on.
  if (!delta || delta.length !== part.rest.length || part.morphWeight <= 0) return part.rest
  const k = part.morphWeight / 100
  const out = part.morphed ?? (part.morphed = new Float32Array(part.rest.length))
  for (let i = 0; i < out.length; i++) out[i] = part.rest[i] + delta[i] * k
  return out
}

/**
 * The order the accommodation sequence takes the rings in.
 *
 * **Nose first**, and in this space that is **ascending Z**: `unityPosition`
 * negates Z on the way in (`unity.ts`:27), so the nose sits at -Z — the prefab
 * puts `Lid_Front` at -1.050 and `Lid_Back` at +0.950. Reading the sign off
 * `BUS_HEAD_END_Z` instead gets this backwards: that is a distance along
 * forward, not a coordinate.
 *
 * Pulled out of the builder and given a name so `ringOrder.test.ts` can assert
 * the direction against the prefab. Sorting by the meshes' names does not work:
 * on a Large the nose-most ring is `BusAccommodation (13)`, the highest number.
 */
export function sortRingsNoseFirst<T>(items: readonly T[], z: (item: T) => number): T[] {
  return [...items].sort((a, b) => z(a) - z(b))
}

/** Mean local Z of a part, for ordering the rings along the bus. */
function ringZ(part: Part): number {
  let sum = 0
  let n = 0
  for (let i = 2; i < part.rest.length; i += 3) {
    sum += part.rest[i]
    n++
  }
  return n > 0 ? sum / n : 0
}

const _pose = { position: new Vector3(), tangent: new Vector3() }
const _framePos = new Vector3()
const _forward = new Vector3()
const _right = new Vector3()
const _matrix = new Matrix4()
const UP = new Vector3(0, 1, 0)

/**
 * Frames sampled along the body per bend.
 *
 * ⚠️ The old note here read *"at 48 frames a Large bus samples every 0.3
 * units"*. That was measured in **local Z**, not along the curve — and the two
 * are not the same thing, because the middle stretches. Sampled evenly in Z, a
 * Large bus's frames ran 0.299 to **3.383** units of curve apart, an 11.3×
 * spread, with only 6.7 of the 48 landing in the middle. The frame table is now
 * spaced evenly in `along` instead (see `poseBent`), which puts every frame
 * 0.728 units apart and 31 of them in the middle.
 *
 * ❓ One table where the original has three. `ApplyDeformedSegments` gives each
 * of a bus's three `BusBodyMesh` components its own 33 samples over its own
 * window, so the original's middle step is `roadMiddle / 32` = 0.705 on a
 * Large — 3 % finer than this. Closing that properly means three tables, not a
 * bigger `FRAMES`; the number here is not tuned against it.
 */
const FRAMES = 48
/** (px, py, pz, rightX, rightZ, forwardX) per frame; forwardZ alongside. */
const frames = new Float32Array((FRAMES + 1) * 6)
const framesFz = new Float32Array(FRAMES + 1)
/**
 * `_spineCurvatures` (0x1D0), `_spinePosK` (0x1D8), `_spineNegK` (0x1E0).
 *
 * Kept as three parallel arrays rather than folded into the interleaved table,
 * which is how `BusBodyMesh` holds them — the patch plan suggested widening the
 * stride to 8 instead, but that would have put two of the three somewhere the
 * original does not have them and left the names further from the decomp.
 *
 * Body only. The rings ride the curve without the middle's stretch and `b2.2`
 * excludes them from the narrowing, so they need no curvature.
 */
const frameK = new Float32Array(FRAMES + 1)
const framePosK = new Float32Array(FRAMES + 1)
const frameNegK = new Float32Array(FRAMES + 1)
/** Gap between frame `i` and `i + 1`, which both the curvature and the slope
 * limit divide by. */
const frameStep = new Float32Array(FRAMES)
/** `_spineClearPos` (0x1F0) and `_spineClearNeg` (0x1F8). */
const frameRowPercent = new Float64Array(FRAMES + 1)
const clearProbes = createClearanceProbes()
const _probePose = { position: new Vector3(), tangent: new Vector3() }
const frameClearPos = new Float32Array(FRAMES + 1)
const frameClearNeg = new Float32Array(FRAMES + 1)
/** The same, for the rings, which ride the curve without the middle's stretch. */
const ringFrames = new Float32Array((FRAMES + 1) * 6)
const ringFramesFz = new Float32Array(FRAMES + 1)

interface Part {
  mesh: Mesh
  /** Straight-along-Z reference copy, already in bus-local space. */
  rest: Float32Array
  restNormals: Float32Array | null
  /**
   * Under `Parts/Head`, `Parts/Middle` or `Parts/Tail` in the prefab — the
   * three transforms `ExitHiddenState` scales on Y for the reveal bounce
   * (`headPart` 0x98, `tailPart` 0xA0, `middleBody.transform` 0xA8). Decided
   * from the hierarchy **before** the pieces are flattened onto the root, since
   * afterwards nothing remembers where a mesh hung. `Lids` and
   * `Accommodations` are siblings of `Parts`, so they do not bounce.
   */
  bounce: boolean
  /** A roof panel `Bus.OpenCover` takes off when the merge is accepted. */
  lid: boolean
  /**
   * Dropped by `EnterSplineRideMode`, which disables every renderer in
   * `renderersToHideInRideMode` as the bus reaches the road.
   *
   * That list did not survive the export — all seven MonoBehaviours in the
   * prefab come out with no serialized fields at all — so what is in it comes
   * from the shipped game's behaviour. Two things are:
   *
   *  - **The ribbed rings.** Each `BusAccommodation` uses the 0.33-unit
   *    `BusCenter` mesh, and that mesh **is roofed**: 16 of its triangles span
   *    the centreline, all at y 4.02–4.18. Ten of them tiled make a continuous
   *    corrugated lid over the middle, which is what kept the cabin looking
   *    shut long after every actual `Lid_` was hidden. They are
   *    `SkinnedMeshRenderer`s in `Bus.accommodationObjects` with their own
   *    animation, and they belong to the parked look.
   *
   *    They are *not* load-bearing: `Bus_Front`, `BusCenter` and `Bus_Back`
   *    tile exactly — for a Medium, [-5.46,-1.04], [-1.04,0.96], [0.96,5.15] —
   *    so the body is closed without them. The rings only overlay the join.
   *  - **`Lid_Back`.** `OpenCover` only clears `headCoverTransform` and
   *    `middleCoverTransform`, never `tailCoverTransform`, so on that evidence
   *    alone the rear panel would stay — but the shipped game shows the rear
   *    open on the road, and ride mode is the mechanism that does it.
   *
   * The middle block itself stays: measured by spanning triangles rather than
   * by vertices, `BusCenter_0/_1/_2` has **no roof at all** — its only
   * cross-cabin surfaces are the floor, at y 0.06 and 0.58. Hiding it, which
   * this did for a while, took the floor out from under the passengers.
   */
  hiddenInRideMode: boolean
  /** `Lid_Back` — dropped by the sequence's `OnComplete`. See `TAIL_LID`. */
  tailLid: boolean
  /** One of the `BusAccommodation` rings, driven by the staggered sequence. */
  ring: boolean
  /** A headlight ray, on from the moment the bus starts driving. */
  light: boolean
  /**
   * Current `Anim` blend shape weight, 0–200, as the original's tween feeds
   * `SetBlendShapeWeight`. Only meaningful on a ring.
   */
  morphWeight: number
  /**
   * The `Anim` deltas, in bus-local space, or null if the mesh has none.
   *
   * Baked through the same matrix as `rest`, so it can be added straight on
   * before the body is bent — the order Unity uses, where the skinned mesh
   * resolves its blend shape and the result is what gets drawn.
   */
  morphDelta: Float32Array | null
  /** `rest` with the current morph applied. Reused; only written when it moves. */
  morphed: Float32Array | null
}

interface BusVisual {
  node: Group
  parts: Part[]
  /**
   * Seconds since the bus entered ride mode, or -1 while still parked. Drives
   * the staggered ring sequence; the lids do not use it.
   */
  rideTime: number
  /** The rings, nose first, in the order the sequence takes them. */
  rings: Part[]
  /**
   * The second deformed copy, for a body that straddles the seam, or null
   * until one is needed. `RebuildImmediate`'s two segments; see `poseBent`.
   */
  seam: Part[] | null
  /** Local Z extent across the whole assembly, for mapping onto the curve. */
  minZ: number
  maxZ: number
  /** Where the nose piece ends and the tail piece begins, in the same space. */
  frontEnd: number
  backStart: number
  bent: boolean
  /**
   * 🔍 `ExitHiddenState`:86-133 — the Y scale the session view hands over each
   * frame from the reveal sequence's clock (`revealScaleYAt`); 1 when no
   * sequence is running. Applied to the `bounce` parts only.
   */
  revealScaleY: number
  /** The rigid vertices currently hold a scaled copy and need resetting. */
  revealScaled: boolean
}

/**
 * Where a vertex at baked local `z` sits **along the curve**, measured back from
 * the head point the way `bodyPose` counts.
 *
 * 🔴 Shared, and that is the point. The emitters used to guess this — the smoke
 * was placed at `bodyPose(grownBodyLength)` on the assumption that it is the
 * tail. It is not: `grownBodyLength` is the end of the **middle** section, and
 * the tail cap hangs a further `capBackRoom` behind it. So the exhaust came out
 * a whole cap length too far forward, which on a straight road merely looks
 * wrong and on a curve puts it **on the bodywork** — worse the longer the bus,
 * because the cap is longer. Running the emitters through the same map the mesh
 * uses is the only way they cannot disagree with it.
 *
 * The cap keeps its shape but is squeezed into whatever room it has —
 * `BusBodyMesh` normalises the source's Z to [0,1] and maps it onto the clip
 * window, so a half-clipped cap is a half-length cap, not a cut one.
 */
export function alongForZ(
  z: number,
  minZ: number,
  span: number,
  capFront: number,
  capBack: number,
  middle: number,
  roadMiddle: number,
  capBackRoom: number,
): number {
  const d = z - minZ
  if (d <= capFront) return d - capFront
  if (d >= span - capBack) {
    return roadMiddle + ((d - (span - capBack)) / capBack) * capBackRoom
  }
  return ((d - capFront) / middle) * roadMiddle
}

/**
 * Which turn of the loop a second copy of the body has to be drawn on, or 0 if
 * one copy is enough.
 *
 * 🔍 The test in `BusBodyMesh.RebuildImmediate` (`__0x20fec80.c`:209): a clip
 * window with `clipFrom <= clipTo` is one segment, and one with
 * `clipFrom > clipTo` is two -- `(clipFrom, 1)` and `(0, clipTo)`. In road
 * distance that is the same question as "does the drawn body leave
 * `[0, loopLength]`", and it can only leave at one end: the body is always
 * shorter than the road.
 *
 * `rawMin` is the back of the tail cap and `rawMax` the tip of the nose cap,
 * both as unwrapped road distance.
 */
export function seamBranchFor(loop: number, rawMin: number, rawMax: number): number {
  if (!(loop > 0)) return 0
  if (rawMin < 0) return loop
  if (rawMax > loop) return -loop
  return 0
}

export class BusFleet {
  private readonly visuals = new Map<number, BusVisual>()
  private readonly disposables: { dispose(): void }[] = []

  /**
   * How the body is laid out on the road. Mutable so the HUD sliders take
   * effect on the next frame — the numbers are lost `[SerializeField]`s and
   * have to be found by eye.
   */
  bodyTuning: BusBodyTuning = { ...DEFAULT_BUS_BODY_TUNING }

  constructor(private readonly library: AssetLibrary) {}

  /**
   * Buses whose headlight shafts are inside a tunnel mouth this frame.
   *
   * 🔍 `UpdateInvisibility` hides the bus's renderers **as one object** — the
   * lights included — so the original never draws a shaft beside a hidden bus.
   * The port cuts the body per fragment instead (see `RoadClip`), and the
   * shaft's material is a plain additive one with no clip planes, so it went on
   * glowing in the gap after the body had been cut away.
   *
   * Hidden whole rather than clipped, which is what the original does to it.
   */
  private readonly lightsHidden = new Set<number>()

  /** Called by the view, which is the side that knows where the tunnels are. */
  setLightsHidden(bus: Bus, hidden: boolean): void {
    if (hidden) this.lightsHidden.add(bus.id)
    else this.lightsHidden.delete(bus.id)
  }

  /** One shared additive material for every headlight shaft in the level. */
  private lightRay: ShaderMaterial | null = null

  /**
   * The headlight shaft's material.
   *
   * 🔴 **The beam is not one flat colour — it fades along its length, and the
   * fade lives in the mesh's VERTEX ALPHA.** `SM_LightRay_Round` carries
   * `m_Colors` of white with alpha **255 at the lamp ring (y = 0)** and **0 at
   * the far ring (y = −1.829)**; the legacy Particles/Additive shader multiplies
   * by that vertex colour, so the shaft is bright at the lamp and gone by the
   * tip.
   *
   * The port could not show it: `buildAssets.mjs` writes vertex colours to the
   * OBJ as `v x y z r g b` — **three components** — so the alpha was dropped on
   * the way in and every beam came out a uniform slab. Rather than widen the OBJ
   * format, the ramp is rebuilt in `buildBus` from the same `t` the taper uses:
   * the mesh has exactly two vertex rings, alpha 255 and 0, so a straight
   * `1 − t` **is** the data rather than an approximation of it.
   *
   * Shaped like `skidTrail`'s material on purpose — a `tint` uniform plus a
   * per-vertex `aAlpha` — because `renderPreview`'s software rasteriser already
   * understands both, so this needs no third round of teaching it.
   */
  private lightRayMaterial(): ShaderMaterial {
    if (this.lightRay) return this.lightRay
    const [r, g, b] = BUS_LIGHT_RAY.tint
    const material = new ShaderMaterial({
      uniforms: {
        // sRGB, like every colour read out of a `.mat` (CLAUDE.md §8).
        tint: {
          value: new Color().setRGB(
            r * BUS_LIGHT_RAY.gain,
            g * BUS_LIGHT_RAY.gain,
            b * BUS_LIGHT_RAY.gain,
            'srgb',
          ),
        },
      },
      transparent: true,
      depthWrite: false,
      blending: AdditiveBlending,
      vertexShader:
        'attribute float aAlpha; varying float vA;' +
        'void main() { vA = aAlpha;' +
        ' gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }',
      fragmentShader:
        'uniform vec3 tint; varying float vA;' +
        'void main() { if (vA <= 0.002) discard; gl_FragColor = vec4(tint, vA); }',
    })
    material.name = BUS_LIGHT_RAY.material
    this.lightRay = material
    this.disposables.push(material)
    return material
  }

  /**
   * Where a point at Unity local `z` on this bus lands along its curve.
   *
   * For the particle emitters, so they sit exactly where the drawn mesh puts
   * that slice of the bus — see `alongForZ`. Null when the bus is not built.
   */
  alongForUnityZ(bus: Bus, follower: BusSplineFollower, unityZ: number): number | null {
    const visual = this.visuals.get(bus.id)
    if (!visual) return null
    const minZ = visual.minZ
    const span = visual.maxZ - minZ || 1
    const capFront = Math.max(0, visual.frontEnd - minZ)
    const capBack = Math.max(0, visual.maxZ - visual.backStart)
    const middle = Math.max(1e-4, span - capFront - capBack)
    // Unity +Z is forward and the bake mirrors Z, so a Unity local z is −z here.
    return alongForZ(
      -unityZ,
      minZ,
      span,
      capFront,
      capBack,
      middle,
      follower.grownBodyLength,
      follower.tailCapRoom(capBack),
    )
  }

  /** Every mesh the fleet needs, so they can be prefetched in one go. */
  static requiredMeshes(library: AssetLibrary): string[] {
    const names = new Set<string>()
    for (const prefabName of Object.values(PREFAB)) {
      const prefab = library.prefab(prefabName)
      if (!prefab) continue
      for (const subtree of DRAWN_SUBTREES) {
        const node = findNode(prefab, subtree)
        if (node) library.meshesUsedBy(node, names)
      }
    }
    return [...names]
  }

  build(bus: Bus): Object3D | null {
    const prefab = this.library.prefab(PREFAB[bus.size])
    if (!prefab) return null

    const node = new Group()
    node.name = `bus-${bus.id}-art`

    // `Bus.SwitchColor` — pick the palette material for this bus's colour, the
    // same lookup `GetCarMaterial` / `GetAccommodationMaterial` do — and then
    // 🔍 `Bus.ApplyIceMaterialState`, which puts `IceBus` on **slot 0** of the
    // same renderers while `_type == Ice` (FRZ-8). No shell mesh, no extra
    // slot: an iced bus is its own body in the ice material, and `BreakIce`
    // gets the colour back by rebuilding — `SessionView.sync` re-keys on the
    // type as well as on the colour. See `busBodyMaterials`.
    const colour = bus.visibleColor
    const { body, seats } = busBodyMaterials(colour, bus.type)
    const swap = (name: string): string =>
      BODY_MATERIAL.test(name) ? body : SEAT_MATERIAL.test(name) ? seats : name

    for (const subtree of DRAWN_SUBTREES) {
      const source = findNode(prefab, subtree) as ManifestNode | null
      if (!source) continue
      node.add(
        buildPrefab(this.library, source, {
          material: swap,
          skip: (n) => SKIP.has(n.name),
          // `CarLights` ships switched off and the game switches it on, so the
          // tree has to be built even though the prefab says inactive.
          includeInactive: true,
        }),
      )
    }

    // Bake every part into bus-local space.
    //
    // The pieces sit at their own offsets under `Parts`, `Lids` and
    // `Accommodations`. Flattening them once means the bend can treat the whole
    // bus as a single straight body and never has to walk the hierarchy.
    node.updateMatrixWorld(true)
    const toLocal = new Matrix4().copy(node.matrixWorld).invert()

    // Collect first: `traverse` must not be reparenting as it walks.
    const meshes: Mesh[] = []
    node.traverse((child) => {
      if ((child as Mesh).isMesh) meshes.push(child as Mesh)
    })

    const parts: Part[] = []
    let minZ = Infinity
    let maxZ = -Infinity
    // The cap boundaries: where the nose piece stops and the tail piece starts.
    let frontEnd = -Infinity
    let backStart = Infinity

    // 🔍 `Bus.SwitchColor` (`__0x20f8938.c`):58-65 — a hidden bus is swapped to
    // the **Grey** material (`ColorType.Grey = 9`, `dump.cs:709187`) *and then*
    // `ApplyHiddenMaterialState` is applied to it, in the same branch. The grey
    // half was already right: `visibleColor` returns `None` and `carMaterialName`
    // maps that to `Bus_Gray`. This is the second half — the dissolve wrapper.
    //
    // A cached twin, never a mutation: `material(name)` hands the same object to
    // every bus of that colour. See `AssetLibrary.hidden`.
    if (bus.type === BoxTypeEnum.Hidden) {
      for (const mesh of meshes) {
        const m = mesh.material
        if (m instanceof ToonMaterial) mesh.material = this.library.hidden(m)
        else if (Array.isArray(m)) {
          mesh.material = m.map((one) =>
            one instanceof ToonMaterial ? this.library.hidden(one) : one,
          )
        }
      }
    }

    // 🔍 The headlight shafts. `PolygonParticleFX_LightRay_Cube` is on builtin
    // shader 200 — Unity's legacy Particles/Additive — with a null `_MainTex`
    // and a 13 % amber `_TintColor`, so it is a soft additive glow. Drawn with
    // the ordinary toon material it came out a solid white box on the nose of
    // every bus, which is what put `CarLights` in `SKIP` in the first place.
    //
    // Swapped here rather than in `AssetLibrary` because this is the only
    // material in the export on that shader: teaching the general material path
    // about legacy particle shaders would be a wider change with one caller.
    //
    // The stretch that stands in for the missing skinning is applied further
    // down, on the **cloned** geometry.
    //
    // 🔴 Not here, and the reason is worth keeping: `buildPrefab` hands out the
    // library's geometry, so every bus in the level shares one
    // `SM_LightRay_Round`. Scaling it in place here multiplied it again for each
    // bus — 1.82, then 3.3, then 6.0 — and the headlights came out as beams
    // stretching off the edge of the screen. A shared buffer looks like a local
    // variable right up until the second caller.
    for (const mesh of meshes) {
      if (LIGHTS.test(mesh.name)) {
        mesh.material = this.lightRayMaterial()
        // 📐 The prefab's renderer says On, but the material is on builtin
        // shader 200 (legacy Particles/Additive), which has no ShadowCaster
        // pass — a glow throws no shadow. `applyShadowCasting` decided from the
        // toon material it was handed before the swap, so it is overruled here.
        mesh.castShadow = false
      }
    }

    for (const mesh of meshes) {
      // Read the hierarchy now: the re-parent below erases it.
      const bounce = underBouncePart(mesh, node)
      const geometry = mesh.geometry.clone()
      // The headlight shaft's missing skinning, on this bus's own copy. It has
      // to happen **before** `_matrix`, while the mesh is still in its own
      // space, where the shaft runs along −Y and its radius lies in XZ.
      //
      // 🔴 The mesh is a **cylinder of radius 1** — measured off the OBJ, a
      // ring at y = −1.829 and a capped disc at y = 0, both radius 1.0, no
      // taper anywhere. The cone is made entirely by the bone scales (0.2 at
      // the lamp, 0.2 × 3.7 at the tip). Scaling only the length, which is what
      // this did at first, leaves a fat tube stuck on the nose of every bus.
      // See `BUS_LIGHT_RAY`.
      if (LIGHTS.test(mesh.name)) {
        const position = geometry.getAttribute('position')
        const array = position?.array as Float32Array | undefined
        if (array) {
          const { meshLength, length, nearRadius, farRadius } = BUS_LIGHT_RAY
          const alphas = new Float32Array(array.length / 3)
          for (let i = 0; i < array.length; i += 3) {
            // 0 at the lamp (y = 0), 1 at the far ring (y = −meshLength).
            const t = Math.min(1, Math.max(0, -array[i + 1] / meshLength))
            const radius = nearRadius + (farRadius - nearRadius) * t
            array[i] *= radius
            array[i + 1] = -t * length
            array[i + 2] *= radius
            // The mesh's own vertex alpha — 1 at the lamp ring, 0 at the far
            // one — which the OBJ dropped. See `lightRayMaterial`.
            alphas[i / 3] = (1 - t) * BUS_LIGHT_RAY.alpha * BUS_LIGHT_RAY.gain
          }
          geometry.setAttribute('aAlpha', new BufferAttribute(alphas, 1))
          position!.needsUpdate = true
        }
      }
      _matrix.copy(toLocal).multiply(mesh.matrixWorld)
      geometry.applyMatrix4(_matrix)
      this.disposables.push(geometry)
      mesh.geometry = geometry

      // Re-parent onto the root, not just zero the mesh's own transform.
      //
      // The pieces hang off nested groups — `Parts/Head` at z +1.04,
      // `Accommodations` at z -0.99 — and those matrices are already baked into
      // the geometry. Leaving the mesh under its old parent applies them a
      // second time, which scatters the body and tears it apart once bent.
      node.add(mesh)
      mesh.matrixAutoUpdate = false
      mesh.matrix.identity()
      mesh.position.set(0, 0, 0)
      mesh.quaternion.identity()
      mesh.scale.set(1, 1, 1)
      mesh.frustumCulled = false

      const position = geometry.getAttribute('position') as BufferAttribute
      const normal = geometry.getAttribute('normal') as BufferAttribute | undefined
      const rest = Float32Array.from(position.array as ArrayLike<number>)
      let partMin = Infinity
      let partMax = -Infinity
      for (let i = 2; i < rest.length; i += 3) {
        if (rest[i] < partMin) partMin = rest[i]
        if (rest[i] > partMax) partMax = rest[i]
      }
      // 🔴 The headlight shafts are **not** part of the body's extent.
      //
      // They reach about 1.3 units past the nose, so letting them into
      // `minZ` moved the bus's measured front end forward by that much — and
      // `minZ` is the origin of the whole deformation: `capFront`, `span` and
      // `alongAt` are all measured from it. Adding the lights therefore skewed
      // the mapping for **every part of the bus**, not just for the lights, and
      // it did so silently: the span stayed inside the 35 % sanity check.
      //
      // This only became possible when `CarLights` left `SKIP`. It is the cost
      // of a measurement that quietly takes whatever is in the list.
      if (!LIGHTS.test(mesh.name)) {
        if (partMin < minZ) minZ = partMin
        if (partMax > maxZ) maxZ = partMax
      }
      if (/^Bus_Front#/.test(mesh.name)) frontEnd = Math.max(frontEnd, partMax)
      if (/^Bus_Back#/.test(mesh.name)) backStart = Math.min(backStart, partMin)
      parts.push({
        mesh,
        rest,
        restNormals: normal ? Float32Array.from(normal.array as ArrayLike<number>) : null,
        bounce,
        lid: REMOVED_BY_OPEN_COVER.test(mesh.name),
        hiddenInRideMode: HIDDEN_IN_RIDE_MODE.test(mesh.name),
        tailLid: TAIL_LID.test(mesh.name),
        ring: /^BusAccommodation/.test(mesh.name),
        light: LIGHTS.test(mesh.name),
        morphWeight: 0,
        morphDelta: bakeMorph(this.library, mesh.userData.meshName as string | null, _matrix),
        morphed: null,
      })
    }

    // The empty groups the pieces came out of would otherwise still carry their
    // transforms and confuse anything walking the tree later.
    for (const child of [...node.children]) {
      if (!(child as Mesh).isMesh) node.remove(child)
    }

    if (parts.length === 0) return null

    // The assembly should be about as long as the bus. Anything wildly longer
    // means a transform got applied twice and the pieces are scattered — which
    // is exactly what happened when the meshes were left under their original
    // groups. Cheap to check, and the symptom is otherwise a torn mesh that
    // only shows up once a bus starts bending.
    const span = maxZ - minZ
    if (span > bus.length * 1.35 || span < bus.length * 0.65) {
      console.warn(
        `[bus] #${bus.id} assembly spans ${span.toFixed(2)}u but the bus is ${bus.length.toFixed(2)}u` +
          ' — the parts are probably double-transformed.',
      )
    }

    // Fall back to the whole assembly if a piece is missing, so the mapping
    // degrades to the rigid one rather than producing nonsense.
    if (!Number.isFinite(frontEnd)) frontEnd = minZ
    if (!Number.isFinite(backStart)) backStart = maxZ

    // The sequence runs down the bus, so order the rings by position. A bus
    // already on the road when its art is built — a level reload mid-run, or
    // the checks — starts past the end of the sequence rather than replaying it.
    const rings = sortRingsNoseFirst(
      parts.filter((p) => /^BusAccommodation/.test(p.mesh.name)),
      ringZ,
    )
    const alreadyGone = bus.coverOpen || bus.isOnRoad

    this.visuals.set(bus.id, {
      node,
      parts,
      minZ,
      maxZ,
      frontEnd,
      backStart,
      seam: null,
      bent: false,
      revealScaleY: 1,
      revealScaled: false,
      rideTime: alreadyGone ? Infinity : -1,
      rings,
    })
    return node
  }

  /**
   * 🔍 `Bus.ExitHiddenState` (`__0x20f91bc.c`:86-133) — `DOScaleY` on
   * `headPart` / `tailPart` / `middleBody.transform`, as this frame's scale.
   *
   * The session view owns the sequence's clock (it is the same sequence the
   * material tween runs on) and calls this before posing, every frame, so a
   * finished or killed sequence lands here as 1 and the parts go back to rest.
   * The original scales three `Transform`s about their own origins; here the
   * pieces are baked into bus-local space, so the scale is applied to the
   * vertices' local Y — see `poseRigid` / `poseBranch` — about **y = 0**:
   * 📐 in all three `ConveyorBox (S|M|L).prefab` the `Head`, `Middle` and `Tail`
   * transforms sit at `y 0`, unrotated, unscaled, under `Parts` at the origin
   * (`hiddenRevealBounce.test.ts` reads them), so their origin is the bus's
   * ground plane and scaling their Y is scaling local Y.
   */
  setRevealScaleY(bus: Bus, scaleY: number): void {
    const visual = this.visuals.get(bus.id)
    if (visual) visual.revealScaleY = scaleY
  }

  has(bus: Bus): boolean {
    return this.visuals.has(bus.id)
  }

  /**
   * The node the shipped art hangs off, so `PlayFailCrash`'s roll can be
   * applied on top of whichever pose ran this frame.
   *
   * It is a **sibling** of the marker box the session view keeps, not a child,
   * which is exactly the trap the first attempt at the roll fell into: rotating
   * the marker's node turned the debug box and left the bus standing.
   */
  drawnNode(bus: Bus): Object3D | null {
    return this.visuals.get(bus.id)?.node ?? null
  }

  /**
   * Local Z extent of the first ring with its blend shape applied.
   *
   * Bus-local, before the bend, so it isolates the deformation: measuring the
   * posed geometry instead mixes in the curve's heading and the middle's
   * stretch, and a world-space bounding box on a turning bus tells you almost
   * nothing about how long the ring is.
   */
  ringRestSpan(bus: Bus): number {
    const visual = this.visuals.get(bus.id)
    const ring = visual?.rings[0]
    if (!ring) return 0
    const rest = restOf(ring)
    let min = Infinity
    let max = -Infinity
    for (let i = 2; i < rest.length; i += 3) {
      if (rest[i] < min) min = rest[i]
      if (rest[i] > max) max = rest[i]
    }
    return max - min
  }

  /**
   * Along-curve length of the first ring **as drawn**.
   *
   * Distance between the posed positions of its rearmost and foremost rest
   * vertices. That is the number that catches the ring being dragged through
   * the middle's stretch: it should track `ringRestSpan`, and a body-stretched
   * ring comes out about five times longer.
   */
  ringPosedLength(bus: Bus): number {
    const visual = this.visuals.get(bus.id)
    const ring = visual?.rings[0]
    // A hidden ring is skipped by the pose loop, so its buffer still holds
    // whatever it had when it was last drawn. Reading that back reports a
    // length from an earlier frame as if it were this one.
    if (!ring || !ring.mesh.visible) return 0

    // Centroid of the front face against centroid of the back face.
    //
    // Taking the two extreme-Z vertices instead measures a diagonal: the ring
    // is a hoop nearly 3.9 wide, and its foremost and rearmost vertices sit on
    // opposite sides of it, so that distance is dominated by the width and
    // reports about 4.6 for a 0.33-unit ring however it is posed.
    const rest = ring.rest
    let minZ = Infinity
    let maxZ = -Infinity
    for (let i = 2; i < rest.length; i += 3) {
      if (rest[i] < minZ) minZ = rest[i]
      if (rest[i] > maxZ) maxZ = rest[i]
    }
    const band = (maxZ - minZ) * 0.1 || 1e-4
    const out = ring.mesh.geometry.getAttribute('position').array as Float32Array
    const front = [0, 0, 0]
    const back = [0, 0, 0]
    let nf = 0
    let nb = 0
    for (let i = 0; i < rest.length; i += 3) {
      const target = rest[i + 2] <= minZ + band ? front : rest[i + 2] >= maxZ - band ? back : null
      if (!target) continue
      target[0] += out[i]
      target[1] += out[i + 1]
      target[2] += out[i + 2]
      if (target === front) nf++
      else nb++
    }
    if (nf === 0 || nb === 0) return 0
    return Math.hypot(
      front[0] / nf - back[0] / nb,
      front[1] / nf - back[1] / nb,
      front[2] / nf - back[2] / nb,
    )
  }

  /** Highest `Anim` weight any ring is currently at. For the checks. */
  peakMorphWeight(bus: Bus): number {
    const visual = this.visuals.get(bus.id)
    if (!visual) return 0
    let peak = 0
    for (const ring of visual.rings) peak = Math.max(peak, ring.morphWeight)
    return peak
  }

  /**
   * What the renderer currently has for this bus — for the runtime trace.
   *
   * Reads the flags back off the meshes rather than recomputing them, so it
   * reports what is really being drawn, not what should be.
   */
  debugInfo(bus: Bus): {
    capFront: number
    capBack: number
    ringsVisible: number
    lidsVisible: number
    morph: number
    rideTime: number
  } | null {
    const visual = this.visuals.get(bus.id)
    if (!visual) return null
    let ringsVisible = 0
    let lidsVisible = 0
    for (const part of visual.parts) {
      if (part.ring && part.mesh.visible) ringsVisible++
      if (part.lid && part.mesh.visible) lidsVisible++
    }
    return {
      capFront: Math.max(0, visual.frontEnd - visual.minZ),
      capBack: Math.max(0, visual.maxZ - visual.backStart),
      ringsVisible,
      lidsVisible,
      morph: this.peakMorphWeight(bus),
      rideTime: visual.rideTime,
    }
  }

  /** Draw a parked bus: rigid, at the bus's own transform. */
  poseRigid(bus: Bus): void {
    const visual = this.visuals.get(bus.id)
    if (!visual) return

    // Rewrite the vertices when they are not at rest: after a bend, or while
    // the reveal bounce holds a scaled copy (or just stopped holding one).
    const scaleY = visual.revealScaleY
    if (visual.bent || scaleY !== 1 || visual.revealScaled) {
      for (const part of visual.parts) {
        const sy = part.bounce ? scaleY : 1
        const position = part.mesh.geometry.getAttribute('position') as BufferAttribute
        const out = position.array as Float32Array
        out.set(restOf(part))
        const normal = part.mesh.geometry.getAttribute('normal') as BufferAttribute | undefined
        const outNormals = normal && part.restNormals ? (normal.array as Float32Array) : null
        if (outNormals) outNormals.set(part.restNormals!)
        if (sy !== 1) {
          // `DOScaleY` on the part's transform: local Y about the part's own
          // origin, which is y = 0 (see `setRevealScaleY`). A normal under a
          // non-uniform scale goes through the inverse transpose — Y divided,
          // then renormalised.
          for (let i = 1; i < out.length; i += 3) out[i] *= sy
          if (outNormals) scaleNormalsY(outNormals, sy)
        }
        position.needsUpdate = true
        if (normal && outNormals) normal.needsUpdate = true
        part.mesh.geometry.computeBoundingSphere()
      }
      visual.bent = false
      visual.revealScaled = scaleY !== 1
    }

    visual.node.position.copy(bus.position)
    visual.node.quaternion.copy(bus.rotation)
    visual.node.visible = bus.state !== 'Gone'
    this.applyCover(bus, visual, false)
  }

  /**
   * Two separate switch-offs, on two separate triggers.
   *
   * `OpenCover` takes the head and middle roof panels off when the merge is
   * accepted, and they never come back. `EnterSplineRideMode` drops the parked
   * middle block and the rear lid as the bus reaches the road — that is what
   * `onSpline` selects, and it is why a parked bus still has its rear panel.
   */
  private applyCover(bus: Bus, visual: BusVisual, onSpline: boolean): void {
    // Hoisted above the part loop because the rear lid needs them too — its
    // trigger is the end of this same wave.
    const total = this.bodyTuning.accommodationAnimationSeconds
    const count = visual.rings.length
    const morph = total * RING_MORPH_FRACTION
    const stagger = count > 1 ? (total - morph) / (count - 1) : 0
    const t = visual.rideTime

    // Lids: a bare `SetActive(false)`, no tween, in the original too — but they
    // go as the bus leaves its slot, not when it merges, so the change is well
    // clear of the moment it swings onto the road.
    for (const part of visual.parts) {
      if (part.light) part.mesh.visible = bus.lightsOn && !this.lightsHidden.has(bus.id)
      else if (part.lid) part.mesh.visible = !bus.coverOpen
      else if (part.tailLid) part.mesh.visible = tailLidVisible(t, total, count)
      else if (part.hiddenInRideMode && !part.ring) part.mesh.visible = !onSpline
    }

    // Rings: the staggered sequence. Each is still on until its own tween runs
    // out, so they clear nose-first down the bus.
    for (let i = 0; i < count; i++) {
      const part = visual.rings[i]
      if (t < 0) {
        part.mesh.visible = true
        part.morphWeight = 0
        continue
      }
      const local = t - i * stagger
      part.mesh.visible = local < morph
      // `DOVirtual.Float(0, 200, ...)`, fed straight to `SetBlendShapeWeight`.
      part.morphWeight = local <= 0 ? 0 : Math.min(200, (local / morph) * 200)
    }
  }

  /**
   * Advance the accommodation sequence's clock.
   *
   * Called once per frame before posing, so a bus that reaches the road mid
   * frame starts its wave from that frame.
   *
   * `paused` is `Bus.PauseAccommodationAnimation` /
   * `ResumeAccommodationAnimation`, which are nothing more than
   * `TweenManager.Pause` / `.Play` on the sequence the bus holds at 0x170 —
   * so freezing this clock **is** the port of them.
   */
  advance(bus: Bus, dt: number, onSpline: boolean, paused = false): void {
    const visual = this.visuals.get(bus.id)
    if (!visual) return
    // `PlayAccommodationAnimation` is called from `BusSplineFollower.Initialize`
    // — the moment the bus takes the spline, not the earlier moment it pulls out
    // of its slot. Starting the clock on `coverOpen` instead ran the whole wave
    // during the drive-out, so by the time the rings were drawn bent they were
    // already at full weight and nothing appeared to unfold.
    if (!onSpline) {
      visual.rideTime = -1
      return
    }
    if (paused) return
    visual.rideTime = visual.rideTime < 0 ? 0 : visual.rideTime + dt
  }

  /**
   * Draw a bus riding a curve: the whole assembly bent to follow it.
   *
   * Vertices are written in world space and the node reset to the origin,
   * because a bent body has no single rigid transform. Local Z maps to distance
   * along the curve, X to the curve's right, Y straight up — the frame the
   * original's `BusBodyMesh` extrudes along.
   */
  poseBent(bus: Bus, follower: BusSplineFollower): boolean {
    const visual = this.visuals.get(bus.id)
    if (!visual) return false

    visual.node.visible = bus.state !== 'Gone'
    visual.node.position.set(0, 0, 0)
    visual.node.quaternion.identity()
    this.applyCover(bus, visual, true)
    // 🔍 `BusBodyMesh.RebuildImmediate` (`__0x20fec80.c`:207-262) is where a
    // body that straddles the seam becomes **two** pieces, and it is the whole
    // of this fix:
    //
    //     if (clipFrom <= clipTo)   segments = [ (clipFrom, clipTo) ]
    //     else                      segments = [ (clipFrom, 1), (0, clipTo) ]
    //
    // -- each with its own `AddCap`, so the two ends are closed and nothing is
    // drawn between them. The bus really is at both tunnel mouths at once.
    //
    // This port draws one deformed copy per segment. A segment is a branch of
    // the wrap: `branch = 0` is `(0, clipTo)`, clamped at the far end so the
    // overflow piles up at spline distance `loopLength`; `branch = loopLength`
    // is `(clipFrom, 1)`, clamped at `0`. Each pile sits at the deepest point of
    // a tunnel stub, which is inside the box `RoadClip` discards -- and
    // `roadEndsReached` has already turned **both** mouths on for exactly this
    // case. So each copy shows only its own segment, and the union is the bus.
    const branch = this.seamBranch(visual, follower)
    let artChanged = false
    this.poseBranch(visual, follower, visual.parts, 0)
    if (branch !== 0) {
      artChanged = this.ensureSeamParts(visual)
      const seam = visual.seam
      if (seam) {
        // The twin is the same assembly, so it has to answer to the same
        // `applyCover` decisions the primary just took.
        //
        // 🔴 **Except the lamps, which the twin never gets.** The shaft is a
        // rigid piece pinned to its own copy's nose, and the twin's nose is
        // `clamp(head ± loopLength)` — always the deep end of a stub, always
        // inside a tunnel, by construction of `seamBranchFor`. Its material is
        // a plain additive one with no window to cut against, so the beam was
        // drawn whole out there: two shafts glowing on bare ground behind a
        // cover, which is what was still visible after the body itself
        // stopped escaping. `setLightsHidden` cannot answer for it — that
        // flag is measured at the **primary's** nose, which in exactly this
        // state is back on tarmac and rightly lit.
        for (let i = 0; i < seam.length; i++) {
          seam[i].mesh.visible = visual.parts[i].mesh.visible && !seam[i].light
        }
        this.poseBranch(visual, follower, seam, branch)
      }
    } else if (visual.seam) {
      for (const part of visual.seam) part.mesh.visible = false
    }

    visual.bent = true
    return artChanged
  }

  /**
   * Which wrap branch a second copy of the body would have to be drawn on, or
   * 0 if the body fits between the two ends of the spline and one copy is
   * enough.
   *
   * The drawn body spans `raw` from `headDistance + capFront` at the nose down
   * to `headDistance - (grownBodyLength + tailCapRoom)` at the back of the tail
   * cap. It straddles when that window leaves `[0, loopLength]` at either end,
   * and it can only leave at one of them: the window is always shorter than the
   * road.
   */
  private seamBranch(visual: BusVisual, follower: BusSplineFollower): number {
    if (!follower.ridesMainSpline) return 0
    const capBack = Math.max(0, visual.maxZ - visual.backStart)
    const head = follower.roadHeadDistance
    return seamBranchFor(
      follower.loopLength,
      head - (follower.grownBodyLength + follower.tailCapRoom(capBack)),
      head + Math.max(0, visual.frontEnd - visual.minZ),
    )
  }

  /**
   * The twin set of meshes the seam branch is drawn into.
   *
   * Built on the first straddle rather than up front: most buses on most levels
   * never reach a tunnel, and an unbuilt twin costs nothing. Returns true when
   * it built them, so the caller can tell the session view to re-collect the
   * meshes it clips -- the twin needs the tunnel material swap as much as the
   * primary does, and more, since it is the copy that lives inside a mouth.
   */
  private ensureSeamParts(visual: BusVisual): boolean {
    if (visual.seam) return false
    const seam: Part[] = []
    for (const part of visual.parts) {
      const mesh = new Mesh(part.mesh.geometry.clone(), part.mesh.material)
      mesh.name = `${part.mesh.name}-seam`
      mesh.frustumCulled = false
      mesh.renderOrder = part.mesh.renderOrder
      mesh.visible = false
      // The twin is the same body, so it throws the same shadow (§8: every
      // per-bus decision has to be asked again for the copy) — and the same
      // *cut* shadow: `copyShadowCasting` carries the road-cut depth material
      // too, and `SessionView.setBusClipped` re-derives it for the twin once
      // `recollectMeshes` has picked it up. (This used to claim 🔍 that the
      // original casts from a stub because the light's culling mask is every
      // layer; the mask is real, but whether that shadow shows is decided by
      // the stripped stencil feature, so it is ❓ — see `roadCutShadow.ts`.)
      copyShadowCasting(part.mesh, mesh)
      this.disposables.push(mesh.geometry)
      visual.node.add(mesh)
      // `rest`, `restNormals` and `morphDelta` are read-only reference copies:
      // shared on purpose, so a twin cannot drift from its primary.
      seam.push({ ...part, mesh, morphed: null })
    }
    visual.seam = seam
    return true
  }

  private poseBranch(
    visual: BusVisual,
    follower: BusSplineFollower,
    parts: readonly Part[],
    branch: number,
  ): void {

    // Where a vertex sits along the body comes from the **assembly's own
    // extent**, not from `BUS_CENTRE_Z`.
    //
    // The two disagree, and the assembly is the one that describes the drawn
    // bus: the three pieces span 10.62 units centred at +0.15 on a Medium,
    // which is what `HeadEnd` (+5.22) and `TailEnd` (-4.84) bracket.
    // `BUS_CENTRE_Z` was measured off `Bus_N_single`, the hidden `Ice/Fake`
    // mesh, and sits 1.3 units away from all of that.
    const minZ = visual.minZ
    const span = visual.maxZ - minZ || 1

    // The road layout, straight out of `UpdateCapBodies`.
    //
    //     HeadBody: clipFrom = headPercent
    //               clipTo   = headPercent + headCapWorldLength / splineLength
    //     TailBody: clipFrom = tailPercent - tailCapWorldLength / splineLength
    //               clipTo   = tailPercent
    //
    // So the two caps sit **outside** the head..tail span, each as long as its
    // own source mesh (`InitCapBodies` reads the cap length straight back out of
    // `BusBodyMesh._sourceWorldLength`), and the middle is regenerated to fill
    // head..tail between them. A bus on the road is therefore drawn
    // `headCap + bodyLength + tailCap` long. `bodyLength` is `busMiddleSpan` —
    // `GetSplineLengthPercent(size) * splineLength` — so the concertina in the
    // middle really does stretch, and by more on a longer road.
    //
    // Nothing here is tuned: the cap lengths are the measured pieces and the
    // middle is 2.0 on every prefab. `bodyPose(along)` counts back from the head
    // point, so the nose cap lives at negative `along`.
    const capFront = Math.max(0, visual.frontEnd - minZ)
    const capBack = Math.max(0, visual.maxZ - visual.backStart)
    const middle = Math.max(1e-4, span - capFront - capBack)
    const roadMiddle = follower.grownBodyLength

    // The tail cap is the piece that grows.
    //
    // `UpdateCapBodies` clips it against the start of the curve, so a bus still
    // coming out of the lot has its drawn tail pinned at the entry point while
    // the nose and body drive on — the bus lengthens rather than sliding out
    // whole. The middle does **not** do this: `Initialize` places the bus fully
    // formed over its parked footprint, so shrinking the middle (which this did
    // for a while) just made the whole bus short.
    const capBackRoom = follower.tailCapRoom(capBack)

    // Only the map from local Z to distance-along changes. The frame table is
    // still sampled uniformly in Z, so each frame carries the curve position
    // its own slice belongs at and the piecewise kinks land inside a frame.
    const alongAt = (z: number): number =>
      alongForZ(z, minZ, span, capFront, capBack, middle, roadMiddle, capBackRoom)

    // 🔍 The frame table is spaced evenly **along the curve**, not evenly in
    // local Z.
    //
    // ⚠️ `ApplyDeformedSegments__0x2101184.c`:286 does **not** walk `i / 32.0`.
    // This comment said so for a long time and it drops two terms. Verbatim,
    // :272-274 and :286:
    //
    //     pad   = 0f; if (worldLength > 0.5f) pad = min(2f / worldLength, 0.5f);
    //     denom = 1f + 2f * pad;
    //     t     = denom * (i / 32f) - pad;          // i = 0..32
    //
    // so the rows run from **-pad to 1+pad**, overshooting both ends of the body
    // (RV-2.5). `i / 32.0` is only the innermost factor.
    // `SPINE_RES + 1 = 33` samples (`dump.cs:709813`) — over the window of
    // **its own** `BusBodyMesh`. And there are **three** of those on every bus:
    // grepping the component guid `5067e4fae859499e2708f7a901eb3bfc` returns 3
    // in each of `ConveyorBox (S/M/L).prefab`, one per body piece. So the
    // original gives the middle its own 33 samples over its own arc, however
    // short that piece is in local Z.
    //
    // This port keeps one table for the whole bus, which is fine — but it used
    // to space the frames uniformly in Z and then push each through `alongAt`.
    // `alongAt` is piecewise with wildly different slopes (1 through the caps,
    // `roadMiddle / middle` through the middle), so the middle's share of the
    // frames was `FRAMES × middle / span`: on a Large bus, 2.0 of 14.36, or
    // **6.7 frames** for a stretch that after the unfolding fix spans most of
    // the body. Each of those frames covered several units of curve, and a bend
    // sampled that coarsely reads as a faceted, bunched concertina.
    //
    // Spacing by `along` instead costs nothing and puts the resolution where
    // the curvature is.
    const alongMin = -capFront
    const alongSpan = Math.max(1e-4, roadMiddle + capBackRoom - alongMin)
    const bodyFrameAlong = (f: number): number => frameAlong(f, alongMin, alongSpan)
    const bodyFrameIndex = (z: number): number => frameIndex(alongAt(z), alongMin, alongSpan)

    /**
     * The rings do not stretch with the middle, and they hang off the **tail**.
     *
     * They are `SkinnedMeshRenderer`s in `Bus.accommodationObjects`, siblings of
     * the body rather than part of it, so `BusBodyMesh`'s deformation never
     * touches them — they keep their authored size and simply bend along the
     * curve. Running them through `alongAt` stretched each 0.33-unit ring to
     * 1.68 and turned the concertina into a set of wide bands.
     *
     * 📐 **Which end they are pinned to is the whole animation.** This used to
     * measure from the nose (`z - minZ - capFront`), and that is wrong in a way
     * a screenshot shows instantly: the nose runs away during the unfolding, so
     * nose-pinned rings ran away with it and the new cabin appeared as a blank
     * panel **behind** them, against the rear lid. The bus grew in the wrong
     * place.
     *
     * Pinned to the tail instead, the concertina stays where the tail is pinned
     * and the nose pulls away from it, so the cabin opens up **exactly where
     * each ring was** as the wave clears them nose-first — which is the motion
     * the whole mechanism is for.
     *
     * Three things say tail, and none of them alone would be enough:
     *  - `BusSplineFollower.Initialize` (`__0x2110984.c`:401-411) reads
     *    `accommodationParent.localPosition` (`Bus` 0x70) together with
     *    `tailPart.localPosition` and `Inverse(tailPart.rotation)` — it relates
     *    the assembly to the **tail part**, and to nothing else. Ghidra dropped
     *    the assignment, so the operation is inferred, not read: hence 📐.
     *  - Nothing re-poses it afterwards. 0x70 is touched in `Initialize` alone,
     *    and `Bus.AccommodationParent` has no call site (checked with
     *    `_plus_0x` matches filtered out, per CLAUDE.md §8).
     *  - `reference/gameplay.mp4`, level 8, the blue Medium leaving its slot:
     *    through 2.30-2.40 the surviving ribs sit against the **rear**, and the
     *    open cabin is ahead of them. Nose-pinning predicts the opposite.
     *
     * Identical to the old expression whenever the bus is not stretched
     * (`roadMiddle == middle`, `capBackRoom == capBack`), which is every frame
     * after the unfolding ends — so this only moves the rings during the wave.
     */
    const alongRigid = (z: number): number =>
      ringAlong(z, minZ, span, roadMiddle, capBackRoom)

    // Sample the curve once into a frame table, then interpolate per vertex.
    // Evaluating per vertex would mean a search and a lerp for each of ~10,000
    // vertices per bus per frame. Two tables, because the body and the rings
    // map local Z onto the curve differently.
    const fill = (
      alongOfFrame: (f: number) => number,
      into: Float32Array,
      intoFz: Float32Array,
    ): void => {
      for (let f = 0; f <= FRAMES; f++) {
        // Clamped, not wrapped: two samples either side of the spline seam are
        // at opposite corners of the level, and the triangles between them
        // smear across the whole map. See `bodyPose`.
        follower.bodyPose(alongOfFrame(f), _pose, true, branch)
        convertPosition(_pose.position, _framePos)
        convertPosition(_pose.tangent, _forward)
        _forward.y = 0
        if (_forward.lengthSq() < 1e-9) _forward.set(0, 0, -1)
        _forward.normalize()
        _right.crossVectors(_forward, UP).normalize()

        const b = f * 6
        into[b] = _framePos.x
        into[b + 1] = _framePos.y
        into[b + 2] = _framePos.z
        into[b + 3] = _right.x
        into[b + 4] = _right.z
        into[b + 5] = _forward.x
        intoFz[f] = _forward.z
      }
    }
    fill(bodyFrameAlong, frames, framesFz)
    // `_spineCurvatures` then its envelope, in the original's order. Nothing
    // reads them yet: `b2.2` is what narrows the body with them, and this is
    // the table it will read. See `frameCurvature`.
    frameK[0] = 0
    for (let f = 1; f <= FRAMES; f++) {
      const a = (f - 1) * 6
      const b = f * 6
      const dx = frames[b] - frames[a]
      const dy = frames[b + 1] - frames[a + 1]
      const dz = frames[b + 2] - frames[a + 2]
      frameStep[f - 1] = Math.sqrt(dx * dx + dy * dy + dz * dz)
      frameK[f] = frameCurvature(
        frames[a + 5],
        framesFz[f - 1],
        frames[b + 5],
        framesFz[f],
        frameStep[f - 1],
      )
    }
    curvatureEnvelope(frameK, framePosK, frameNegK, FRAMES + 1)

    // 🔍 `BusSplineFollower.UpdateClearanceProbes` (`__0x210e800.c`), which the
    // original calls from `LateUpdate` before the body is rebuilt. The window
    // reaches a cap length past each end of the body, so the probes see curve
    // the spine rows do not.
    //
    // `_headCapWorldLength` / `_tailCapWorldLength` are the drawn cap lengths —
    // `capFront` and the room the tail cap actually has, the same two numbers
    // `alongAt` maps the caps onto.
    const splineLength = follower.cachedCurrentSplineLength
    const headPercent = splineLength > 0 ? follower.headDistance / splineLength : 0
    const tailPercent =
      splineLength > 0 ? (follower.headDistance - roadMiddle) / splineLength : 0
    updateClearanceProbes(
      clearProbes,
      splineLength,
      headPercent,
      tailPercent,
      capFront,
      capBackRoom,
      (percent, into) => {
        // `bodyPose` counts back from the head; the probes are given as percent
        // on the same curve, so the two meet through `headDistance`.
        follower.bodyPose(
          follower.headDistance - percent * splineLength,
          _probePose,
          true,
          branch,
        )
        convertPosition(_probePose.position, into)
      },
    )
    for (let f = 0; f <= FRAMES; f++) {
      frameRowPercent[f] =
        splineLength > 0 ? (follower.headDistance - bodyFrameAlong(f)) / splineLength : 0
    }

    clearanceChannels(
      framePosK,
      frameNegK,
      frameStep,
      frameClearPos,
      frameClearNeg,
      FRAMES + 1,
      BUS_SOURCE_HALF_WIDTH,
      frames,
      framesFz,
      clearProbes,
      frameRowPercent,
    )
    let ringFramesFilled = false

    for (const part of parts) {
      // A hidden ring costs nothing to skip, and most of them are hidden for
      // most of the wave.
      if (!part.mesh.visible) continue

      if (part.ring && !ringFramesFilled) {
        fill((f) => alongRigid(minZ + (span * f) / FRAMES), ringFrames, ringFramesFz)
        ringFramesFilled = true
      }
      const table = part.ring ? ringFrames : frames
      const tableFz = part.ring ? ringFramesFz : framesFz

      const geometry = part.mesh.geometry
      const position = geometry.getAttribute('position') as BufferAttribute
      const out = position.array as Float32Array
      const rest = restOf(part)
      const normal = geometry.getAttribute('normal') as BufferAttribute | undefined
      const outNormals = normal ? (normal.array as Float32Array) : null
      const restNormals = part.restNormals
      // 🔍 `ExitHiddenState`:86-133 — the reveal bounce on the three body
      // parts, as `poseRigid` applies it: local Y about y = 0, before the
      // frame's up is added. A bus is `Parked` when it is revealed, so this is
      // only reached when the player dispatches it inside the 0.358 s.
      const sy = part.bounce ? visual.revealScaleY : 1

      // Where each vertex sits **along the road**, which is what decides
      // whether it is drawn at all — see `RoadWindow`. Written here rather than
      // derived in the shader because this is the one place that knows the map
      // from local Z onto the curve, and it is a different map per part.
      const roadOut = roadDistanceAttribute(geometry, rest.length / 3)
      const headDistance = follower.roadHeadDistance + branch

      for (let i = 0; i < rest.length; i += 3) {
        const restX = rest[i]
        const y = rest[i + 1] * sy
        const z = rest[i + 2]

        // Rings still index by local Z, because their table is still filled by
        // Z — they do not stretch with the middle, so an even-in-Z table is
        // exactly right for them. The body indexes by `along`, matching its own
        // table. Getting these two out of step is what the comment above the
        // `alongRigid` block warns about, and it has been paid for once.
        // 🔍 A headlight shaft is **not** deformed. `ApplyPositions`
        // (`__0x210f044.c`:286-345) walks `_cachedLights` (0x180) separately
        // from the body: for each it does `SplineComputer.Travel(_headPercent)`,
        // `Evaluate`, and `SetPositionAndRotation` from that one sample plus the
        // light's cached `LocalOffset` / `LocalRotation`. So every light rides a
        // single frame — the one at the nose — as a rigid piece.
        //
        // Indexing it by its own Z like body geometry is wrong twice over: the
        // shaft reaches 3.33 units **past** the nose, so most of it falls off
        // the end of the table and extrapolates, and even the part that does not
        // gets bent around a corner the shaft has no business bending around.
        // On a curve that threw the two beams out to either side of the bus.
        //
        // 🔴 The nose is **minZ**, not maxZ. In this baked local space the front
        // is the LOW end: `capFront = visual.frontEnd - minZ` measures the nose
        // cap up from minZ, and `alongAt(minZ)` returns `-capFront`, i.e. ahead
        // of the head point. Anchoring the lights to maxZ pinned them to the
        // TAIL frame and pushed them a body-length further back again — so on a
        // curve, and at a stop, the beams sat somewhere behind the bus entirely.
        // A parked bus never showed it because it does not come through here.
        const t = part.light
          ? bodyFrameIndex(visual.minZ)
          : part.ring
            ? ((z - minZ) / span) * FRAMES
            : bodyFrameIndex(z)
        let x = restX
        let f0 = Math.floor(t)
        if (f0 < 0) f0 = 0
        if (f0 > FRAMES - 1) f0 = FRAMES - 1
        const k = t - f0
        const a = f0 * 6
        const b = a + 6

        const px = table[a] + (table[b] - table[a]) * k
        const py = table[a + 1] + (table[b + 1] - table[a + 1]) * k
        const pz = table[a + 2] + (table[b + 2] - table[a + 2]) * k
        const rx = table[a + 3] + (table[b + 3] - table[a + 3]) * k
        const rz = table[a + 4] + (table[b + 4] - table[a + 4]) * k
        const fx = table[a + 5] + (table[b + 5] - table[a + 5]) * k
        const fz = tableFz[f0] + (tableFz[f0 + 1] - tableFz[f0]) * k

        // 🔍 The narrowing, applied per vertex the way `ApplyDeformedSegments`
        // does at `:1350-1440`: the channel is read between the two rows the
        // vertex falls between, and the vertex's own side of the body chooses
        // which channel.
        //
        //     if (v.x > 0) v.x =  SoftLimit( v.x, lerp(clearPos[i], clearPos[i+1], frac));
        //     if (v.x < 0) v.x = -SoftLimit(-v.x, lerp(clearNeg[i], clearNeg[i+1], frac));
        //
        // Rings are left alone. They ride the curve without the middle's
        // stretch and are not part of the deformed strip the original narrows.
        if (!part.ring) {
          if (x > 0) {
            const lim =
              frameClearPos[f0] + (frameClearPos[f0 + 1] - frameClearPos[f0]) * k
            x = softLimit(x, lim)
          } else if (x < 0) {
            const lim =
              frameClearNeg[f0] + (frameClearNeg[f0 + 1] - frameClearNeg[f0]) * k
            x = -softLimit(-x, lim)
          }
        }

        if (part.light) {
          // Rigid: the whole shaft shares the nose's frame, so its own Z has to
          // be carried along the frame's forward axis rather than being folded
          // into the table lookup. `fx`/`fz` is that axis — the same one the
          // normals use below, where local Z maps to −forward.
          // Distance BEHIND the nose, so it moves against the frame's
          // forward axis — the same convention the normals use just below,
          // where local Z maps to −forward.
          const ahead = z - visual.minZ
          out[i] = px + rx * x - fx * ahead
          out[i + 1] = py + y
          out[i + 2] = pz + rz * x - fz * ahead
        } else {
          out[i] = px + rx * x
          out[i + 1] = py + y
          out[i + 2] = pz + rz * x
        }

        // The same `along` the frame lookup just used, turned into a road
        // distance. Unwrapped and unclamped on purpose: a vertex the pose
        // clamped to the end of a stub keeps the distance it *wanted*, which is
        // outside the window, which is exactly why it must not be drawn.
        roadOut[i / 3] =
          headDistance -
          (part.light
            ? alongAt(visual.minZ) + (z - visual.minZ)
            : part.ring
              ? alongRigid(z)
              : alongAt(z))

        if (outNormals && restNormals) {
          const nx = restNormals[i]
          const nz = restNormals[i + 2]
          // Same frame on the normal: local X -> right, Y -> up, Z -> -forward.
          outNormals[i] = rx * nx - fx * nz
          outNormals[i + 1] = restNormals[i + 1]
          outNormals[i + 2] = rz * nx - fz * nz
          if (sy !== 1) {
            // The bounce is a non-uniform scale: Y over `sy`, then renormalise.
            // The frame above is a rotation, so the length was 1 before this.
            const ny = outNormals[i + 1] / sy
            const len = Math.hypot(outNormals[i], ny, outNormals[i + 2]) || 1
            outNormals[i] /= len
            outNormals[i + 1] = ny / len
            outNormals[i + 2] /= len
          }
        }
      }

      position.needsUpdate = true
      if (normal) normal.needsUpdate = true
      ;(geometry.getAttribute('aRoadDistance') as BufferAttribute).needsUpdate = true
      geometry.computeBoundingSphere()

    }
  }

  hide(bus: Bus): void {
    const visual = this.visuals.get(bus.id)
    if (visual) visual.node.visible = false
  }

  dispose(): void {
    for (const d of this.disposables) d.dispose()
    this.disposables.length = 0
    this.visuals.clear()
  }
}
